/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {
  forwardRef, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useLocation} from 'react-router-dom';
import PropTypes from 'prop-types';

import Link from '@salesforce/retail-react-app/app/components/link';
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive';
import {
  Accordion, AccordionButton, AccordionItem, AccordionPanel, Box, Button, Fade,
  Flex, Text, useBreakpointValue, useMultiStyleConfig, useTheme, VStack,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import {useAccessToken, usePromotions, useProducts, useCommerceApi} from '@salesforce/commerce-sdk-react';

import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants';
import {useCurrency} from '@salesforce/retail-react-app/app/hooks';
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation';
import {useDerivedProduct} from '@amblique/king-living/app/hooks/use-derived-product';
import {useAddToCartModalContext} from '@salesforce/retail-react-app/app/hooks/use-add-to-cart-modal';
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast';
import {
  getFinishedGoodCoverVariantId,
  getFinishedGoodMasterProductId,
  getPriceData,
  getProductEffectivePrice,
  getProductListPrice,
  getOptionalAddOns,
  hasProductSpecificationsContent,
  isSimpleRawProduct,
  openNotifyMeForm,
  SIMPLE_PRODUCT_TYPE,
} from '@amblique/king-living/app/utils/product-utils';
import {GET_PRODUCTS_PARAMS, GET_PRODUCTS_STALE_TIME_MS, resolveCoverVariantViewDetailsProduct} from '@amblique/king-living/app/utils/api-utils';
import {useCommittedCoverVariantProduct} from '@amblique/king-living/app/hooks/use-committed-cover-variant-product';
import {setPdpConfigOverlayDocumentOpen} from '@amblique/king-living/app/hooks/use-pdp-config-overlay-open';
import {buildSteps, isCustomizeJourneyComplete} from '@amblique/king-living/app/hooks/use-customize-steps';
import {
  buildChildCartItems,
  buildCustomizeAtcSuccessPayload,
  buildPrimaryCartItem,
  computeCustomizeTotal,
  resolveUpsellNavigationProductIds,
  computeCustomizeListTotal,
  requiresCoverForConfiguredImage,
  resolveCustomizeAtcConfiguredImageUrl,
  resolveCustomizeAtcDisplayPrice,
  resolveKingGuardSourceProduct,
} from '@amblique/king-living/app/utils/cart-utils';
import {getCoverGroups, getAllCoverVariants, getSelectedVariantProduct, filterVisibleOptionProducts} from '@amblique/king-living/app/utils/customize-selections';
import {getInventory, INVENTORY_STATUS} from '@amblique/king-living/app/utils/inventory-utils';
import {
  buildCoverSelectionFromVariant,
  useFreeformCoverPreselect,
} from '@amblique/king-living/app/hooks/use-freeform-cover-preselect';
import {
  buildLayersFromSelections,
  resolveCommittedSelectionGalleryResource,
  resolveGalleryResource,
  resolveGalleryResourceForProduct,
  resolveViewDetailsOptionGalleryResource,
} from '@amblique/king-living/app/utils/gallery-resource-utils';
import {
  hasCommittedSelectionData,
  resolveMostRecentCommittedOptionProductId,
  resolveSimplePdpHeroDriverFromCommittedSelections,
  resolveSimplePdpMainGalleryState,
  resolveSimplePdpMainViewDetailsProduct,
  resolveSimplePdpShowGallerySummary,
} from '@amblique/king-living/app/utils/gallery-state-utils';
import {
  getStickyHeaderHeightPx,
  scrollToWithHeaderOffset,
} from '@amblique/king-living/app/utils/utils';
import {formatCurrencyHideTrailingZeroCents} from '@amblique/king-living/app/utils/format-currency-hide-zero-cents';

import {PlusIcon, PromotionAccordionMinusIcon} from '@amblique/king-living/app/components/icons';
import BaseInformationSection from '@amblique/king-living/app/components/product-view/base-information-section';
import ProductStockIndicator from '@amblique/king-living/app/components/product-stock-indicator';
import {Skeleton as ImageGallerySkeleton} from '@amblique/king-living/app/components/image-gallery';
import ImageGalleryV2 from '@amblique/king-living/app/components/image-gallery-v2';
import AddToCartStrip, {PDP_PRICE_SECTION_ID} from '@amblique/king-living/app/components/product-view/add-to-cart-strip';
import {useInPageCtaVisibility} from '@amblique/king-living/app/hooks/use-in-page-cta-visibility';
import BuyNowPayLaterMessaging, {BNPL_PAGE_TYPE} from '@amblique/king-living/app/components/buy-now-pay-later';
import ConfigurationSelector from '@amblique/king-living/app/components/configuration-selector';
import ReviewSection from '@amblique/king-living/app/components/configuration-selector/review-section';
import ViewDetails from '@amblique/king-living/app/components/configuration-selector/view-details';
import {ChooseYourSets} from '@amblique/king-living/app/components/complex-customize';
import {
  CustomizeStepsInline,
  CustomizeStepsInlineDesktopPicker,
  CustomizeStepsInlineProvider,
} from '@amblique/king-living/app/components/complex-customize/customize-steps-inline';
import {ConfigurableMasterView} from '@amblique/king-living/app/components/configurable-master';
import {CoverSwatchGrid} from '@amblique/king-living/app/components/cover-swatch-grid';
import {
  buildCommittedCoverMirrorPayload,
  CoverSwatchOverflowFlyout,
} from '@amblique/king-living/app/components/cover-swatch-grid/cover-swatch-overflow-flyout';
import {FinishedGoodView} from '@amblique/king-living/app/components/finished-good';
import {PdpDeliveryAvailability} from '@amblique/king-living/app/components/shipping/pdp-delivery-availability';
import InformationAccordion from '@amblique/king-living/app/pages/product-detail/partials/information-accordion';
import {
  getProductUrl,
  WantItSoonerPromo,
  WantItSoonerCarousel,
  useWantItSoonerRef,
} from '@amblique/king-living/app/components/want-it-sooner';
import {useConfigurationProducts} from '@amblique/king-living/app/hooks/use-configuration-products';
import {useProduct} from '@amblique/king-living/app/hooks/use-product';
import {useCommittedConfiguredImageUrl} from '@amblique/king-living/app/hooks/use-committed-configured-image-url';
import {usePdpCustomizeCtaState} from '@amblique/king-living/app/hooks/use-pdp-customize-cta-state';
import {usePdpInventoryGate} from '@amblique/king-living/app/hooks/use-pdp-inventory-gate';
import {usePdpDepositLine} from '@amblique/king-living/app/hooks/use-pdp-deposit-line';
import {usePdpShopperInventoryIds} from '@amblique/king-living/app/hooks/use-pdp-shopper-inventory-ids';
import {useAppMainPdpKind} from '@amblique/king-living/app/hooks/use-app-main-pdp-kind';
import {usePdpCustomizeMirrors} from '@amblique/king-living/app/hooks/use-pdp-customize-mirrors';
import {CustomizeJourneyContext} from '@amblique/king-living/app/context/customize-journey-context';
import OptionalAddOnsContainer from '@amblique/king-living/app/components/product-view/optional-add-ons/optional-add-ons-container';
import {ProtectYourCover} from '@amblique/king-living/app/components/product-view/optional-add-ons/protect-your-cover';
import {useProductDetailContentful} from '@amblique/king-living/app/context/product-detail-contentful-context';
import {
  buildDesktopPdpFlyoutFrozenProps,
  useHeaderContext,
} from '@amblique/king-living/app/contexts/header-context';

/**
 * Add-to-cart button for any PDP that uses the customize journey:
 *  - Complex PDP (guided): cover variant selection drives the primary basket line.
 *  - Simple PDP w/ configurable options (KINDW-1119): there is no cover step, so the
 *    master product itself drives the primary basket line. Option selections still
 *    flow through `c_selectedProducts` / `c_selectedOptionsUI` and child line items
 *    just like Complex.
 */
const ComplexAddToCartButton = forwardRef(function ComplexAddToCartButton(
  {
    addCustomizeToCart,
    currency,
    intl,
    isBasketLoading,
    isCtaPending = false,
    isAddToCartBlocked = false,
    isSimplePdpFlow = false,
    onSuccess,
    product,
    styles,
  },
  ref
) {
  const ctx = useContext(CustomizeJourneyContext);
  const committedSelection = ctx?.committedSelection;
  const resetJourney = ctx?.resetJourney;
  const setProductsMap = ctx?.setProductsMap;
  const setOptionPriceMap = ctx?.setOptionPriceMap;
  const setOptionListPriceMap = ctx?.setOptionListPriceMap;

  const coverVariant = useCommittedCoverVariantProduct(product);

  // Pricing/parent-id base: cover variant for Complex, master product for Simple PDP.
  const customizeBaseProduct = isSimplePdpFlow ? product : coverVariant;

  // KingGuard option data: prefer variant-level options from c_allVariantsData
  // (has variant-specific pricing); fall back to master product options (loaded
  // with expand=options on PDP) when c_allVariantsData hasn't been rebuilt yet.
  // Resolved separately from `customizeBaseProduct` so Simple PDPs (which keep
  // the master as their base) still read the correct per-variant KingGuard price
  // when computing totals — the master's option model misses those overrides.
  const kingGuardSourceProduct = useMemo(
    () => resolveKingGuardSourceProduct(product, coverVariant),
    [coverVariant, product]
  );

  const requireCoverForImage = requiresCoverForConfiguredImage({
    committedMode: committedSelection?.mode,
    isSimplePdpFlow,
  });

  const cylindoUrl = useCommittedConfiguredImageUrl(product, {
    requireCover: requireCoverForImage,
  });

  const primaryItem = useMemo(
    () => buildPrimaryCartItem(
      committedSelection?.selections,
      kingGuardSourceProduct,
      product,
      cylindoUrl,
      committedSelection,
      setProductsMap
    ),
    [
      committedSelection,
      committedSelection?.selections,
      kingGuardSourceProduct,
      product,
      cylindoUrl,
      setProductsMap
    ]
  );

  const childItems = useMemo(
    () => buildChildCartItems(
      committedSelection?.selections,
      product?.id ?? product?.productId ?? null
    ),
    [committedSelection?.selections, product]
  );

  const total = useMemo(
    () => computeCustomizeTotal(committedSelection?.selections, customizeBaseProduct, true, kingGuardSourceProduct, setOptionPriceMap),
    [committedSelection?.selections, customizeBaseProduct, kingGuardSourceProduct, setOptionPriceMap]
  );

  const listTotal = useMemo(
    () => computeCustomizeListTotal(committedSelection?.selections, customizeBaseProduct, true, kingGuardSourceProduct, setOptionListPriceMap),
    [committedSelection?.selections, customizeBaseProduct, kingGuardSourceProduct, setOptionListPriceMap]
  );

  const steps = useMemo(() => {
    if (!product) return [];

    if (committedSelection?.mode === 'set' && committedSelection?.productId) {
      const linkedProduct = setProductsMap?.[committedSelection.productId];

      if (linkedProduct) return buildSteps(product);
    }

    return buildSteps(product, isSimplePdpFlow ? {isSimplePDP: true} : undefined);
  }, [product, committedSelection?.mode, committedSelection?.productId, setProductsMap, isSimplePdpFlow]);

  const isComplete = useMemo(() => {
    if (!product) return false;

    return isCustomizeJourneyComplete(committedSelection?.selections, steps, {
      skipCoverStep: isSimplePdpFlow,
    });
  }, [product, committedSelection?.selections, steps, isSimplePdpFlow]);

  const displayPrice = resolveCustomizeAtcDisplayPrice({
    complexTotal: total,
    masterProductPrice: product?.price ?? 0,
  });

  const formattedPrice = displayPrice > 0 ?
    formatCurrencyHideTrailingZeroCents(intl, displayPrice, currency) :
    '';

  const buttonLabel = `${intl.formatMessage({
    defaultMessage: 'Add to cart',
    id: 'product_view.button.add_to_cart',
  })}${formattedPrice ? ` - ${formattedPrice}` : ''}`;

  const showToast = useToast();

  const handleClick = useCallback(async () => {
    if (isAddToCartBlocked || !isComplete || !primaryItem) return;

    try {
      const result = await addCustomizeToCart(primaryItem, childItems, {
        displayTotal: total,
        optionPriceMap: setOptionPriceMap,
      });

      if (result) {
        const configuredImageUrl = resolveCustomizeAtcConfiguredImageUrl({
          masterProduct: product,
          selections: committedSelection?.selections,
          primaryItem,
          requireCover: requireCoverForImage,
        });

        onSuccess(buildCustomizeAtcSuccessPayload({
          product,
          configuredImageUrl,
          primaryProductId: primaryItem.productId,
          total,
          listTotal,
          totalQty: 1 + childItems.length,
          selections: committedSelection?.selections,
          committedSelection,
        }));
        resetJourney?.();
      }
    } catch (e) {
      showToast({title: intl.formatMessage(API_ERROR_MESSAGE), status: 'error'});
    }
  }, [
    isComplete,
    primaryItem,
    childItems,
    addCustomizeToCart,
    product,
    committedSelection,
    committedSelection?.selections,
    requireCoverForImage,
    onSuccess,
    resetJourney,
    showToast,
    intl,
    total,
    setOptionPriceMap
  ]);

  return (
    <Button
      ref={ref}
      {...styles.actionButton}
      isDisabled={!isComplete || isCtaPending || isAddToCartBlocked}
      isLoading={isCtaPending || isBasketLoading}
      onClick={handleClick}
    >
      {buttonLabel}
    </Button>
  );
});

ComplexAddToCartButton.displayName = 'ComplexAddToCartButton';

ComplexAddToCartButton.propTypes = {
  addCustomizeToCart: PropTypes.func.isRequired,
  currency: PropTypes.string.isRequired,
  intl: PropTypes.object.isRequired,
  isAddToCartBlocked: PropTypes.bool,
  isBasketLoading: PropTypes.bool,
  isCtaPending: PropTypes.bool,
  isSimplePdpFlow: PropTypes.bool,
  onSuccess: PropTypes.func.isRequired,
  product: PropTypes.object,
  styles: PropTypes.object.isRequired,
};

/**
 * Shared price block used by both Complex and Simple PDPs (KINDW-1119/§5).
 *
 * Renders the canonical PDP price UI:
 *   Full price / Sale price label (+ sale amount in sale red when on sale)
 *   $amount
 *   List/full line when on sale (no strikethrough; no Saving badge)
 *
 * Complex PDPs derive sale/list totals from committed selections + cover variant; Simple PDPs
 * (with or without configurable options) pass the totals straight from priceData / customizeTotal.
 */
function TotalPriceBlock({currency, depositProduct, intl, isDepositLoading, listTotal, saleTotal, styles}) {
  const hasPrice = saleTotal && saleTotal > 0;
  const saving = hasPrice && listTotal && listTotal > saleTotal ? listTotal - saleTotal : 0;
  const isOnSale = saving > 0;
  const formattedListTotal = formatCurrencyHideTrailingZeroCents(intl, listTotal, currency);
  const salePriceColor = isOnSale ? 'secondary.newSaleRed' : undefined;

  // Deposit line — shows "{percentage} deposit from {amount}" when the active
  // product is NOT in stock (made-to-order) and deposit-eligible. Rules + math
  // mirror the checkout Payment Amount section (post-promotion `saleTotal`).
  // `depositProduct` is the FULL SCAPI doc the PDP already fetched for the active
  // entity (no extra call here) — see `usePdpDepositLine`.
  const {showDepositLine, depositAmount, formattedDepositPercentage} = usePdpDepositLine({
    entity: depositProduct,
    saleTotal,
    isLoading: isDepositLoading,
  });

  return (
    <Box {...styles.totalPriceInfo}>
      <Box {...styles.totalPriceRow}>
        <Box {...styles.totalPriceDetails}>
          <Text {...styles.totalPriceLabel} color={salePriceColor}>
            {isOnSale ? (
              <FormattedMessage
                defaultMessage="Sale price"
                id="product_view.price.sale_price"
              />
            ) : (
              <FormattedMessage
                defaultMessage="Full price"
                id="product_view.price.full_price"
              />
            )}
          </Text>

          {hasPrice && (
            <Text {...styles.totalPriceValue} color={salePriceColor}>
              {formatCurrencyHideTrailingZeroCents(intl, saleTotal, currency)}
            </Text>
          )}
        </Box>
      </Box>

      {isOnSale && (
        <Box {...styles.totalPricePackageInfo}>
          <Text {...styles.totalPriceListValue}>
            <FormattedMessage
              defaultMessage="Full price"
              id="product_view.price.full_price"
            />
            {' '}
            <Text as="span" {...styles.totalPriceListValueAmount}>
              {formattedListTotal}
            </Text>
          </Text>
        </Box>
      )}

      {showDepositLine && (
        <Text {...styles.totalPriceDeposit}>
          <FormattedMessage
            defaultMessage="{percentage} deposit from {amount}"
            id="product_view.price.deposit"
            values={{
              amount: formatCurrencyHideTrailingZeroCents(intl, depositAmount, currency),
              percentage: formattedDepositPercentage,
            }}
          />
        </Text>
      )}
    </Box>
  );
}

TotalPriceBlock.propTypes = {
  currency: PropTypes.string.isRequired,
  depositProduct: PropTypes.object,
  intl: PropTypes.object.isRequired,
  isDepositLoading: PropTypes.bool,
  listTotal: PropTypes.number,
  saleTotal: PropTypes.number,
  styles: PropTypes.object.isRequired,
};

/**
 * Complex (guided PDP) pricing — resolves sale/list totals from CustomizeJourneyContext, then
 * defers to the shared {@link TotalPriceBlock} so Complex and Simple PDPs render identical UI.
 */
function ComplexTotalPrice({currency, depositProduct, intl, isDepositLoading, product, styles}) {
  const ctx = useContext(CustomizeJourneyContext);
  const committedSelection = ctx?.committedSelection;
  const setOptionPriceMap = ctx?.setOptionPriceMap;
  const setOptionListPriceMap = ctx?.setOptionListPriceMap;
  const coverVariant = useCommittedCoverVariantProduct(product);

  const complexTotal = useMemo(
    () => computeCustomizeTotal(committedSelection?.selections, coverVariant, true, null, setOptionPriceMap),
    [committedSelection?.selections, coverVariant, setOptionPriceMap]
  );

  const saleTotal = resolveCustomizeAtcDisplayPrice({
    complexTotal,
    masterProductPrice: product?.price ?? 0,
  });

  // List total MUST be summed component-by-component (cover + options + add-ons + King
  // Guard) — the SAME item set as `complexTotal` (saleTotal) — so the saving badge equals
  // `total list-price − total sale-price`. Previously this preferred the set card's
  // `originalPrice` (the package RRP), which excludes King Guard / add-ons that ARE in
  // saleTotal, producing an inconsistent (understated) saving. Mirrors the sticky
  // add-to-cart strip and the server-side `c_savingTotal` used by the cart flyout.
  // See docs/product/pricing-architecture.md §4h.
  const listTotal = useMemo(
    () => computeCustomizeListTotal(committedSelection?.selections, coverVariant, true, null, setOptionListPriceMap),
    [committedSelection?.selections, coverVariant, setOptionListPriceMap]
  );

  return (
    <TotalPriceBlock
      currency={currency}
      depositProduct={depositProduct ?? coverVariant ?? product}
      intl={intl}
      isDepositLoading={isDepositLoading}
      listTotal={listTotal}
      saleTotal={saleTotal}
      styles={styles}
    />
  );
}

ComplexTotalPrice.propTypes = {
  currency: PropTypes.string.isRequired,
  depositProduct: PropTypes.object,
  intl: PropTypes.object.isRequired,
  isDepositLoading: PropTypes.bool,
  product: PropTypes.object,
  styles: PropTypes.object.isRequired,
};

function KingGuardSlot({product}) {
  const coverVariant = useCommittedCoverVariantProduct(product);

  return <ProtectYourCover product={product} selectedVariantProduct={coverVariant} />;
}

KingGuardSlot.propTypes = {
  product: PropTypes.object.isRequired,
};

/**
 * Wraps variations column + desktop inline picker so picker React tree is not under
 * `data-flyout-frozen` (KL-998 — same pattern as CoverSwatchOverflowFlyout).
 */
function InlineCustomizeShell({children, desktopPicker, enabled, sessionProps}) {
  if (!enabled) {
    return children;
  }

  return (
    <CustomizeStepsInlineProvider {...sessionProps}>
      <>
        {children}
        {desktopPicker}
      </>
    </CustomizeStepsInlineProvider>
  );
}

InlineCustomizeShell.propTypes = {
  children: PropTypes.node.isRequired,
  desktopPicker: PropTypes.node,
  enabled: PropTypes.bool.isRequired,
  sessionProps: PropTypes.object.isRequired,
};

/**
 * Render a product detail view that includes name, image gallery, price,
 * variant selections, action buttons
 */

const ProductViewContent = forwardRef(
  (
    {
      product,
      category,
      showFullLink = false,
      imageSize = 'md',
      addCustomizeToCart,
      addToCart,
      updateCart,
      isProductLoading,
      isProductPartOfSet = false,
      isProductPartOfBundle = false,
      childOfBundleQuantity = 0,
      childProductOrderability,
      setChildProductOrderability,
      isBasketLoading = false,
      onVariantSelected = () => { },
      validateOrderability = (variant, product, quantity, stockLevel) => {
        if (isProductLoading) return false;
        // Only require a resolved variant when the product has selectable variation axes.
        // type.item products return variants:[] with no variationAttributes — fall back to
        // product-level inventory (matches upstream pwa-kit isStandardProduct handling).
        if (product?.variationAttributes?.length > 0 && !variant) return false;

        const isOrderable = !!(variant?.orderable || product?.inventory?.orderable);

        if (!isOrderable) return false;
        if (quantity <= 0) return false;

        // Perpetual inventory: orderable=true but stockLevel=null/0 → skip qty-vs-stock check.
        const hasFiniteStock = stockLevel > 0;

        return !hasFiniteStock || quantity <= stockLevel;
      },
      showImageGallery = true,
      setSelectedBundleQuantity = () => { },
      selectedBundleParentQuantity = 1,
      onNotifyMeClick,
      addToWishlist,
      isWishlistLoading = false,
    },
    ref
  ) => {
    const {currency: activeCurrency} = useCurrency();
    const showToast = useToast();
    const intl = useIntl();
    const location = useLocation();
    const navigate = useNavigation();
    const {
      isOpen: isAddToCartModalOpen,
      onClose: onAddToCartModalClose,
    } = useAddToCartModalContext();

    const {shopperInventoryIds} = usePdpShopperInventoryIds();
    const api = useCommerceApi();
    const {getTokenWhenReady} = useAccessToken();

    const handlePostAddToCart = useCallback((payload) => {
      navigate('/cart/upsell', 'push', {
        addedProduct: payload.product,
        addedProductIds: resolveUpsellNavigationProductIds({
          addedProductIds: payload.addedProductIds,
          itemsAdded: payload.itemsAdded,
          product: payload.product,
        }),
        configuredImageUrl: payload.configuredImageUrl ?? null,
        itemsAdded: payload.itemsAdded,
        previousPath: location.pathname + location.search,
      });
    }, [location.pathname, location.search, navigate]);
    const theme = useTheme();
    const {isHeaderHidden} = useHeaderContext();
    const styles = useMultiStyleConfig('ProductDetail', {});

    // SCAPI product-level `productPromotions` only carries `calloutMsg`,
    // `promotionId` and `promotionalPrice` — the full "Promotion Details" markup
    // lives on the Promotion document, so fetch it via Shopper Promotions and
    // key it by id for the PDP promotion accordion below.
    const promotionIds = useMemo(() => {
      const ids = (product?.productPromotions ?? [])
        .map((promotion) => promotion.promotionId)
        .filter(Boolean);

      return [...new Set(ids)];
    }, [product?.productPromotions]);

    const {data: promotionDetailsRes} = usePromotions(
      {parameters: {ids: promotionIds.join(',')}},
      {enabled: promotionIds.length > 0}
    );

    const promotionDetailsById = useMemo(() => {
      if (!promotionDetailsRes?.data) return {};

      return promotionDetailsRes.data.reduce((acc, promotion) => {
        acc[promotion.id] = promotion;

        return acc;
      }, {});
    }, [promotionDetailsRes]);

    const productActionSectionStyles = useMemo(
      () => styles.productActionSection ??
        theme.components?.ProductDetail?.baseStyle?.productActionSection,
      [styles.productActionSection, theme.components?.ProductDetail?.baseStyle?.productActionSection]
    );
    const productActionSectionContentStyles = useMemo(
      () => styles.productActionSectionContent ??
        theme.components?.ProductDetail?.baseStyle?.productActionSectionContent,
      [styles.productActionSectionContent, theme.components?.ProductDetail?.baseStyle?.productActionSectionContent]
    );
    const [showOptionsMessage, toggleShowOptionsMessage] = useState(false);
    const [activeTab, setActiveTab] = useState('build');

    // Configuration selector state (lifted for desktop split-layout support)
    const [isConfigOpen, setIsConfigOpen] = useState(false);
    const [selectedConfigId, setSelectedConfigId] = useState(null);
    const [configPanelMode, setConfigPanelMode] = useState('choose-layout');
    const isDesktop = useBreakpointValue({base: false, lg: true});

    // Add-on details product state (for desktop inline view details)
    const [addOnDetailsProduct, setAddOnDetailsProduct] = useState(null);

    const {
      handleConfigClose: applyPdpCustomizeMirrorOnPanelClose,
      pendingScrollToCustomizeAnchorRef,
    } = usePdpCustomizeMirrors({configPanelMode, isConfigOpen});

    const coverGroups = useMemo(
      () => getCoverGroups(product),
      [product]
    );

    const handleOpenChooseLayout = useCallback(() => {
      setConfigPanelMode('choose-layout');
      setIsConfigOpen(true);
      setAddOnDetailsProduct(null); // Clear add-on details when opening flyout
    }, []);

    const handleOpenCustomize = useCallback(() => {
      setSelectedConfigId(null);
      setConfigPanelMode('customize');
      setIsConfigOpen(true);
      setAddOnDetailsProduct(null); // Clear add-on details when opening flyout
    }, []);

    /**
     * Close the configuration panel. See {@link usePdpCustomizeMirrors} for discard vs commit mirror rules.
     */
    const handleConfigClose = useCallback((discardFlyoutDraft = false, committedMode = null) => {
      applyPdpCustomizeMirrorOnPanelClose(discardFlyoutDraft, committedMode);
      setIsConfigOpen(false);
    }, [applyPdpCustomizeMirrorOnPanelClose]);

    const {
      showLoading,
      showInventoryMessage,
      inventoryMessage,
      quantity,
      setQuantity,
      variant,
      variationParams,
      stockLevel,
      isOutOfStock,
      unfulfillable,
      isSimpleProduct,
      isComplexProduct,
      hasConfigurableOptions,
      isFinishedGood,
      simpleProductType,
    } = useDerivedProduct(product, isProductPartOfSet, isProductPartOfBundle);

    useAppMainPdpKind(isComplexProduct);

    const isSimplePlainProduct = useMemo(() => isSimpleRawProduct(product), [product]);
    // FSD §5 – Freeform (variation-base simple master): covers drive the gallery + cart.
    const isSimpleFreeForm = !isComplexProduct && simpleProductType === SIMPLE_PRODUCT_TYPE.FREE_FORM;
    // Configurable Master (Simple master + variations + recommendations) — same product family
    // as Freeform but with extra option steps (e.g. MYCO-SD_TBL_TIMB_490_LTH: covers + finish).
    const isSimpleConfigurableMaster = !isComplexProduct && simpleProductType === SIMPLE_PRODUCT_TYPE.CONFIGURABLE_MASTER;
    // ANY Simple master that exposes cover variants — used to drive the Freeform-style cover swatch
    // grid (4×2 with overflow) and the Notify Me CTA. For CONFIGURABLE_MASTER this REPLACES the
    // inline "Cover / No selection yet / Choose" slot card (see steps-main-view.jsx).
    const isSimpleMasterWithCovers = isSimpleFreeForm || isSimpleConfigurableMaster;
    const isSimplePdpSettledGalleryFlow = !isComplexProduct && hasConfigurableOptions;

    // CustomizeJourneyContext for simple PDP with configurable options / Freeform
    const ctx = useContext(CustomizeJourneyContext);
    const committedSelection = ctx?.committedSelection;
    const flyoutDraftState = ctx?.flyoutDraftState;
    const inlineSelectionsDraft = ctx?.inlineSelectionsDraft;
    const commitInlineSelections = ctx?.commitInlineSelections;
    const isLoadingSetCards = ctx?.isLoadingSetCards ?? false;
    const isFetchingSetOptionPrices = ctx?.isFetchingSetOptionPrices ?? false;
    const setOptionPriceMap = ctx?.setOptionPriceMap;
    const setOptionListPriceMap = ctx?.setOptionListPriceMap;
    const cancelFlyout = ctx?.cancelFlyout;

    /** Live selections for inventory (inline draft / flyout draft / committed). */
    const selectionsForInventory = useMemo(() => {
      if (flyoutDraftState?.isOpen) {
        return flyoutDraftState.draftSelections ?? null;
      }

      if (inlineSelectionsDraft != null) {
        return inlineSelectionsDraft;
      }

      return committedSelection?.selections ?? null;
    }, [
      flyoutDraftState?.isOpen,
      flyoutDraftState?.draftSelections,
      inlineSelectionsDraft,
      committedSelection?.selections
    ]);
    const [inlineGalleryMirror, setInlineGalleryMirror] = useState({
      product: null,
      selectedVariationAttributes: {},
      viewDetailsProduct: null,
    });
    const [simplePdpHeroDriver, setSimplePdpHeroDriver] = useState('standing');
    const [simplePdpOptionPreviewProduct, setSimplePdpOptionPreviewProduct] = useState(null);
    const lastSettledSimplePdpGalleryRef = useRef(null);
    const hasHydratedSimplePdpGalleryDriverRef = useRef(false);
    const coverFlyoutSessionRef = useRef(null);
    const effectiveSimplePdpOptionPreviewRef = useRef(null);
    const [coverOverflowDraft, setCoverOverflowDraft] = useState(null);

    // Track the most-recently-selected option product per container for "See Details" mobile flyout
    // (one trigger per container — uses this map to feed ReviewSection asOverlay).
    const [seeDetailsOverlayProduct, setSeeDetailsOverlayProduct] = useState(null);
    const [seeDetailsOverlayCoverItemType, setSeeDetailsOverlayCoverItemType] = useState(null);
    const [isInlineCustomizePickerOpen, setIsInlineCustomizePickerOpen] = useState(false);
    const [isCoverOverflowFlyoutOpen, setIsCoverOverflowFlyoutOpen] = useState(false);
    const closeInlinePickerRef = useRef(null);

    const isPdpConfigOverlayOpen = isConfigOpen || isInlineCustomizePickerOpen || isCoverOverflowFlyoutOpen;

    useEffect(() => {
      setPdpConfigOverlayDocumentOpen(isPdpConfigOverlayOpen);

      return () => setPdpConfigOverlayDocumentOpen(false);
    }, [isPdpConfigOverlayOpen]);

    const applySimplePdpGalleryDriver = useCallback((driver, optionPreviewProduct = null) => {
      if (!isSimplePdpSettledGalleryFlow) return;

      setAddOnDetailsProduct(null);
      setSimplePdpHeroDriver(driver);
      setSimplePdpOptionPreviewProduct(driver === 'option' ? (optionPreviewProduct ?? null) : null);
    }, [isSimplePdpSettledGalleryFlow]);

    const handleSimplePdpSlotProductPreview = useCallback((previewProduct) => {
      applySimplePdpGalleryDriver('option', previewProduct);
    }, [applySimplePdpGalleryDriver]);

    const handleSimplePdpInlineCoverCommitted = useCallback(() => {
      applySimplePdpGalleryDriver('cover');
    }, [applySimplePdpGalleryDriver]);

    const handleAddOnDetailsOpen = useCallback((addOnProduct) => {
      setAddOnDetailsProduct(addOnProduct ?? null);

      if (!isSimplePdpSettledGalleryFlow) return;

      setSimplePdpHeroDriver('add-on');
      setSimplePdpOptionPreviewProduct(null);
    }, [isSimplePdpSettledGalleryFlow]);

    const handleSimplePdpViewDetails = useCallback((overlayProduct, options = {}) => {
      setSeeDetailsOverlayProduct(overlayProduct ?? null);
      setSeeDetailsOverlayCoverItemType(
        typeof options?.coverItemType === 'string' && options.coverItemType.trim() !== '' ?
          options.coverItemType.trim() :
          null
      );
    }, []);

    const handleSimplePdpViewDetailsClose = useCallback(() => {
      setSeeDetailsOverlayProduct(null);
      setSeeDetailsOverlayCoverItemType(null);
    }, []);

    const handleCoverOverflowGalleryPreview = useCallback((payload) => {
      setAddOnDetailsProduct(null);

      if (isSimplePdpSettledGalleryFlow) {
        const coverProductId =
          payload?.coverProductId ??
          payload?.viewDetailsProduct?.productId ??
          payload?.viewDetailsProduct?.id;

        if (!coverProductId) return;

        setCoverOverflowDraft({
          productId: String(coverProductId),
          scapiProduct: payload?.viewDetailsProduct ?? null,
          variationValues: payload?.selectedVariationAttributes ?? {},
        });
        applySimplePdpGalleryDriver('cover');

        return;
      }

      setInlineGalleryMirror({
        product: payload?.product ?? null,
        selectedVariationAttributes: payload?.selectedVariationAttributes ?? {},
        viewDetailsProduct: payload?.viewDetailsProduct ?? null,
      });
    }, [applySimplePdpGalleryDriver, isSimplePdpSettledGalleryFlow]);

    const coverMasterMinPrice = useMemo(() => {
      try {
        const raw = product?.c_priceProcessed;
        const parsed = raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : {};

        return parsed?.min ?? parsed?.salePrice ?? null;
      } catch {
        return null;
      }
    }, [product]);

    // --- Freeform-style cover swatch grid (KINDW-1121 / KINDW-1124) ------------
    // NOTE: the `freeform*` naming is historical — these derivations apply to ANY Simple master
    // with cover variants (Freeform AND Configurable Master).
    const freeformCoverVariants = useMemo(
      () => (isSimpleMasterWithCovers ? getAllCoverVariants(product) : []),
      [isSimpleMasterWithCovers, product]
    );

    const freeformCoverSelection = committedSelection?.selections?.cover ?? null;
    const committedCoverProductId = freeformCoverSelection?.productId ?? null;

    const {data: committedCoverScapiResult} = useProducts(
      {parameters: {ids: committedCoverProductId ?? '', ...GET_PRODUCTS_PARAMS}},
      {
        enabled: isSimplePdpSettledGalleryFlow && Boolean(committedCoverProductId),
        keepPreviousData: false,
      }
    );
    const committedCoverScapiProduct = committedCoverProductId ?
      (committedCoverScapiResult?.data?.[0] ?? null) :
      null;

    const coverOverflowDraftScapiProduct = useMemo(() => {
      const draftId = coverOverflowDraft?.productId;

      if (!draftId) return null;

      if (committedCoverProductId && String(draftId) === String(committedCoverProductId)) {
        return committedCoverScapiProduct;
      }

      const passed = coverOverflowDraft?.scapiProduct;

      if (!passed) return null;

      const passedId = passed.id ?? passed.productId;

      return passedId != null && String(passedId) === String(draftId) ? passed : null;
    }, [
      committedCoverProductId,
      committedCoverScapiProduct,
      coverOverflowDraft?.productId,
      coverOverflowDraft?.scapiProduct
    ]);

    const isCoverOverflowPreviewActive =
      isCoverOverflowFlyoutOpen && Boolean(coverOverflowDraft?.productId);

    const galleryHeroDriver = isCoverOverflowPreviewActive ? 'cover' : simplePdpHeroDriver;

    const gallerySelectedCoverId = isCoverOverflowPreviewActive ?
      coverOverflowDraft.productId :
      committedCoverProductId;

    const galleryCoverVariationValues = isCoverOverflowPreviewActive ?
      coverOverflowDraft.variationValues :
      freeformCoverSelection?.variationValues;

    const galleryCoverScapiProduct = isCoverOverflowPreviewActive ?
      coverOverflowDraftScapiProduct :
      committedCoverScapiProduct;

    const handleCoverViewDetails = useCallback(async (variant, viewOptions = {}) => {
      const detailProduct = await resolveCoverVariantViewDetailsProduct({
        variant,
        masterProduct: product,
        selectedCoverProductId: committedCoverProductId,
        cachedCoverVariantProduct: galleryCoverScapiProduct,
        shopperProducts: api.shopperProducts,
        getTokenWhenReady,
      });

      handleSimplePdpViewDetails(detailProduct, viewOptions);
    }, [
      api.shopperProducts,
      committedCoverProductId,
      galleryCoverScapiProduct,
      getTokenWhenReady,
      handleSimplePdpViewDetails,
      product
    ]);

    const freeformSelectedVariantEntry = useMemo(() => {
      if (!isSimpleMasterWithCovers || !committedCoverProductId) return null;

      return getSelectedVariantProduct(product, committedCoverProductId);
    }, [committedCoverProductId, isSimpleMasterWithCovers, product]);

    const handleCoverOverflowClose = useCallback(() => {
      setIsCoverOverflowFlyoutOpen(false);
      setCoverOverflowDraft(null);

      if (isSimplePdpSettledGalleryFlow) {
        const session = coverFlyoutSessionRef.current;

        coverFlyoutSessionRef.current = null;

        if (session) {
          setAddOnDetailsProduct(null);
          setSimplePdpHeroDriver(session.heroDriver);
          setSimplePdpOptionPreviewProduct(session.optionPreviewProduct);
        }

        return;
      }

      setInlineGalleryMirror(
        buildCommittedCoverMirrorPayload(product, freeformCoverSelection, freeformCoverVariants)
      );
    }, [
      freeformCoverSelection,
      freeformCoverVariants,
      isSimplePdpSettledGalleryFlow,
      product
    ]);

    const handleCoverOverflowOpen = useCallback(() => {
      if (isSimplePdpSettledGalleryFlow) {
        coverFlyoutSessionRef.current = {
          heroDriver: simplePdpHeroDriver,
          optionPreviewProduct: effectiveSimplePdpOptionPreviewRef.current,
        };
      }

      setIsCoverOverflowFlyoutOpen(true);
    }, [
      isSimplePdpSettledGalleryFlow,
      simplePdpHeroDriver
    ]);

    const freeformHasInStockVariants = useMemo(() => {
      if (!isSimpleMasterWithCovers) return false;

      return freeformCoverVariants.some((entry) => {
        const {canAddToCart, status} = getInventory(product, entry, shopperInventoryIds);

        return canAddToCart && status !== INVENTORY_STATUS.OUT_OF_STOCK;
      });
    }, [isSimpleMasterWithCovers, freeformCoverVariants, product, shopperInventoryIds]);

    // FSD §5.1 / §1.3 — CTA swaps to Notify Me when:
    // - the selected cover is OOS, or
    // - no cover can be selected because no variants are in-stock.
    const freeformIsNotifyMeMode = useMemo(() => {
      if (!isSimpleMasterWithCovers) return false;
      if (freeformSelectedVariantEntry) {
        const {showNotifyMe, status} = getInventory(
          product,
          freeformSelectedVariantEntry,
          shopperInventoryIds
        );

        return showNotifyMe || status === INVENTORY_STATUS.OUT_OF_STOCK;
      }

      return !freeformHasInStockVariants;
    }, [isSimpleMasterWithCovers, freeformSelectedVariantEntry, freeformHasInStockVariants, product, shopperInventoryIds]);

    const useCustomizePathForNotify =
      (isComplexProduct || hasConfigurableOptions || isSimpleFreeForm) && !!addCustomizeToCart;

    const inventoryGateEnabled = !isProductPartOfSet && !isProductPartOfBundle;

    const {
      isAddToCartBlocked,
      isInventoryPending: isPdpInventoryLoading,
      notifyMeMode: customizeNotifyMeMode,
      primaryVariantProduct,
    } = usePdpInventoryGate({
      blockedEnabled: inventoryGateEnabled,
      blockedSelections: selectionsForInventory,
      committedSelection,
      isSimpleMasterWithCovers,
      isSimplePdpFlow: !isComplexProduct && hasConfigurableOptions,
      notifyEnabled: inventoryGateEnabled && useCustomizePathForNotify,
      product,
      variant,
    });

    // Full SCAPI doc for the active entity (selected cover / variant, else the
    // product) the deposit line reads its inventory + `c_isExcludedFromDeposit`
    // from. Reuses the fetch `usePdpInventoryGate` already makes — no extra call.
    const depositSourceProduct = primaryVariantProduct ?? product;

    const {
      showNotifyMe,
      isCtaPending,
      isCtaLoading,
    } = usePdpCustomizeCtaState({
      customizeNotifyMeMode,
      freeformIsNotifyMeMode,
      isBasketLoading,
      isComplexProduct,
      isFetchingSetOptionPrices,
      isInventoryPending: isPdpInventoryLoading,
      isLoadingSetCards,
      isProductLoading,
      useCustomizePath: useCustomizePathForNotify,
    });

    const effectiveShowNotifyMe =
      showNotifyMe ||
      (inventoryGateEnabled && isAddToCartBlocked && !isCtaPending && !isPdpInventoryLoading);

    useFreeformCoverPreselect({
      enabled: isSimpleMasterWithCovers,
      variants: freeformCoverVariants,
      currentSelections: committedSelection?.selections,
      commitInlineSelections,
      productId: product?.id ?? product?.productId ?? null,
    });

    const handleFreeformCoverSelect = useCallback((variant) => {
      if (!variant || typeof commitInlineSelections !== 'function') return;

      coverFlyoutSessionRef.current = null;
      setCoverOverflowDraft(null);

      const coverSelection = buildCoverSelectionFromVariant(variant);
      const baseSelections = committedSelection?.selections && typeof committedSelection.selections === 'object' ?
        committedSelection.selections :
        {};

      commitInlineSelections({
        ...baseSelections,
        cover: coverSelection,
      });

      if (isSimplePdpSettledGalleryFlow) {
        applySimplePdpGalleryDriver('cover');

        return;
      }

      setAddOnDetailsProduct(null);

      // Freeform-only: cover grid outside CustomizeStepsInline — sync mirror on commit.
      setInlineGalleryMirror((prev) => {
        const payload = buildCommittedCoverMirrorPayload(product, coverSelection, freeformCoverVariants);
        const newCoverId = coverSelection?.productId;
        const prevDetailsId = prev.viewDetailsProduct?.productId ?? prev.viewDetailsProduct?.id;
        const isSameCoverPending =
          newCoverId != null && String(prevDetailsId) === String(newCoverId);

        if (!isSameCoverPending) return payload;

        return {
          ...payload,
          viewDetailsProduct: prev.viewDetailsProduct,
        };
      });
    }, [
      applySimplePdpGalleryDriver,
      commitInlineSelections,
      committedSelection?.selections,
      freeformCoverVariants,
      isSimplePdpSettledGalleryFlow,
      product
    ]);

    const handleFreeformNotifyMeClick = useCallback(() => {
      openNotifyMeForm();
    }, []);

    const complexPdpMasterProductId = isComplexProduct ? (product?.id ?? product?.productId ?? null) : null;

    // When the Complex PDP master product changes (client navigation), reset local panel UI and
    // pending customize scroll so state does not leak across masters.
    useEffect(() => {
      if (complexPdpMasterProductId == null || complexPdpMasterProductId === '') {
        return;
      }
      setIsConfigOpen(false);
      setSelectedConfigId(null);
      setConfigPanelMode('choose-layout');
      pendingScrollToCustomizeAnchorRef.current = false;
    }, [complexPdpMasterProductId, pendingScrollToCustomizeAnchorRef]);

    // Clear simple inline gallery mirror when PDP product changes.
    useEffect(() => {
      setInlineGalleryMirror({
        product: null,
        selectedVariationAttributes: {},
        viewDetailsProduct: null,
      });
      setSimplePdpHeroDriver('standing');
      setSimplePdpOptionPreviewProduct(null);
      lastSettledSimplePdpGalleryRef.current = null;
      hasHydratedSimplePdpGalleryDriverRef.current = false;
      setSeeDetailsOverlayProduct(null);
    }, [product?.id, product?.productId]);

    useEffect(() => {
      if (!isSimpleMasterWithCovers || !freeformCoverSelection?.productId) return;

      if (!isSimplePdpSettledGalleryFlow) {
        setAddOnDetailsProduct(null);

        return;
      }

      if (simplePdpHeroDriver === 'standing') {
        applySimplePdpGalleryDriver('cover');
      }
    }, [
      applySimplePdpGalleryDriver,
      freeformCoverSelection?.productId,
      isSimpleMasterWithCovers,
      isSimplePdpSettledGalleryFlow,
      simplePdpHeroDriver
    ]);

    const finishedGoodCoverVariantId = isFinishedGood && getFinishedGoodCoverVariantId(product);
    const {data: finishedGoodCoverVariant, isLoading: isFinishedGoodCoverVariantLoading} = useProduct(
      {parameters: {...GET_PRODUCTS_PARAMS, id: finishedGoodCoverVariantId}},
      {enabled: Boolean(finishedGoodCoverVariantId), staleTime: GET_PRODUCTS_STALE_TIME_MS}
    );
    const finishedGoodMasterProductId = isFinishedGood &&
      getFinishedGoodMasterProductId(finishedGoodCoverVariant ?? null);
    const {data: finishedGoodMasterProduct, isLoading: isFinishedGoodMasterLoading} = useProduct(
      {parameters: {id: finishedGoodMasterProductId, perPricebook: true}},
      {enabled: Boolean(finishedGoodMasterProductId)}
    );
    const layoutProduct = (isFinishedGood && finishedGoodMasterProduct) || product;

    const {configProducts, instockProducts, isLoading: isConfigLoading, isFetched: isConfigProductsFetched} = useConfigurationProducts(product, isComplexProduct, hasConfigurableOptions);
    const showChooseYourLayoutTrigger = isFinishedGood ?
      !product?.c_pimlyHideChooseYourLayout && Boolean(finishedGoodMasterProduct) :
      !product?.c_pimlyHideChooseYourLayout &&
      !isConfigLoading &&
      isConfigProductsFetched &&
      configProducts.length > 0;
    const hasMultipleChooseYourLayoutOptions = configProducts.length > 1;

    const productPromotionAccordion = useMemo(() => {
      const promotions = product?.productPromotions ?? [];

      // Each applied promotion renders as its own collapsible section. The header
      // copy comes from the Promotion Callout and the expandable body from the
      // Promotion Details. Both are authored on the Promotion document, so prefer
      // the full Promotion fetched via Shopper Promotions; the product-level
      // `productPromotions[].calloutMsg` only echoes the raw promotion id when no
      // callout is configured in Business Manager, so it is used solely as a
      // fallback when it is not that raw id.
      const sections = promotions
        .map((promotion, index) => {
          const fullPromotion = promotionDetailsById[promotion?.promotionId];
          const productCalloutMsg = promotion?.calloutMsg?.trim();
          const isRawIdFallback =
            !productCalloutMsg || productCalloutMsg === promotion?.promotionId;
          const calloutMsg =
            fullPromotion?.calloutMsg?.trim() ||
            (isRawIdFallback ? '' : productCalloutMsg);

          // Skip promotions that have no authored callout copy.
          if (!calloutMsg) {
            return null;
          }

          const details = fullPromotion?.details?.trim();

          return {
            key: promotion.promotionId || `promotion-${index}`,
            calloutMsg,
            details,
          };
        })
        .filter(Boolean);

      if (!sections.length) {
        return null;
      }

      return (
        <VStack align="stretch" className="product-view-promotion-accordion" spacing={3} {...styles.promotionAccordion}>
          {sections.map(({key, calloutMsg, details}) => (
            <Accordion key={key} width="100%" allowToggle>
              <AccordionItem {...styles.promotionAccordionItem}>
                {({isExpanded}) => (
                  <>
                    <h3>
                      <AccordionButton className="product-view-promotion-accordion__button" {...styles.promotionAccordionButton}>
                        <Flex {...styles.promotionButtonInner}>
                          <Flex {...styles.promotionContent}>
                            <Box {...styles.promotionDot} />
                            <Text
                              {...styles.promotionTitle}
                              dangerouslySetInnerHTML={{__html: calloutMsg}}
                            />
                          </Flex>
                          {details ? (
                            <Box aria-hidden="true" {...styles.promotionToggleBox}>
                              {isExpanded ? (
                                <PromotionAccordionMinusIcon {...styles.promotionToggleIcon} />
                              ) : (
                                <PlusIcon {...styles.promotionToggleIcon} />
                              )}
                            </Box>
                          ) : null}
                        </Flex>
                      </AccordionButton>
                    </h3>
                    {details ? (
                      <AccordionPanel {...styles.promotionAccordionPanel}>
                        <div dangerouslySetInnerHTML={{__html: details}} />
                      </AccordionPanel>
                    ) : null}
                  </>
                )}
              </AccordionItem>
            </Accordion>
          ))}
        </VStack>
      );
    }, [product?.productPromotions, promotionDetailsById, styles]);

    const wantItSoonerCarouselRef = useWantItSoonerRef();
    const productDetailContentful = useProductDetailContentful();
    const deliveryAvailabilityTooltip =
      typeof productDetailContentful?.deliveryAvailabilityTooltip === 'string' ?
        productDetailContentful.deliveryAvailabilityTooltip.trim() :
        '';

    const showDesktopConfigPanel =
      isDesktop &&
      isConfigOpen &&
      (isComplexProduct || hasConfigurableOptions || isSimpleFreeForm || hasMultipleChooseYourLayoutOptions) &&
      !isProductPartOfSet &&
      !isProductPartOfBundle;

    // Simple PDP inline slot picker (CustomizeStepsInline Drawer) — desktop uses the same
    // flyout shell flag as Complex (frozen RHS variations only; panel is fixed overlay).
    const showInlinePickerFlyoutLayout =
      isInlineCustomizePickerOpen &&
      isSimpleProduct &&
      (hasConfigurableOptions || isSimpleConfigurableMaster) &&
      !isProductPartOfSet &&
      !isProductPartOfBundle;

    const showCoverOverflowFlyoutLayout =
      isCoverOverflowFlyoutOpen &&
      isSimpleMasterWithCovers &&
      !isProductPartOfSet &&
      !isProductPartOfBundle;

    const showInlineCustomizeShell =
      isSimpleProduct &&
      hasConfigurableOptions &&
      !isFinishedGood &&
      !isProductPartOfSet &&
      !isProductPartOfBundle;

    // Desktop only: side-panel flyout layout (gallery + frozen `.variations-section`).
    // Mobile slot/cover pickers use Drawer + useMobilePdpDrawerScrollLock in their own shells
    // (CustomizeStepsInline / CoverSwatchOverflowFlyout / ConfigurationSelector MobileDrawerView).
    const isFlyoutGalleryLayoutActive =
      showDesktopConfigPanel ||
      (showInlinePickerFlyoutLayout && isDesktop) ||
      (showCoverOverflowFlyoutLayout && isDesktop);

    const isConfigurationSelectorOpen =
      isConfigOpen || isInlineCustomizePickerOpen || isCoverOverflowFlyoutOpen;
    const usesFloatingAddToCartStrip =
      (isComplexProduct || hasConfigurableOptions || isSimpleFreeForm) &&
      !isProductPartOfSet &&
      !isProductPartOfBundle;

    const handleDesktopFlyoutBlockedInteract = useCallback(() => {
      if (showDesktopConfigPanel) {
        if (configPanelMode === 'customize') {
          cancelFlyout?.();
          handleConfigClose(true);
        } else {
          handleConfigClose(false);
        }

        return;
      }
      if (showInlinePickerFlyoutLayout && isDesktop) {
        closeInlinePickerRef.current?.();
        setIsInlineCustomizePickerOpen(false);

        return;
      }
      if (showCoverOverflowFlyoutLayout && isDesktop) {
        handleCoverOverflowClose();
      }
    }, [
      showDesktopConfigPanel,
      configPanelMode,
      cancelFlyout,
      handleConfigClose,
      showInlinePickerFlyoutLayout,
      isDesktop,
      showCoverOverflowFlyoutLayout,
      handleCoverOverflowClose
    ]);

    const desktopFlyoutFrozenProps = useMemo(
      () => (isFlyoutGalleryLayoutActive ?
        buildDesktopPdpFlyoutFrozenProps(handleDesktopFlyoutBlockedInteract) :
        {}),
      [isFlyoutGalleryLayoutActive, handleDesktopFlyoutBlockedInteract]
    );

    const desktopFlyoutInteractiveProps = useMemo(
      () => (isFlyoutGalleryLayoutActive ?
        {onClick: (event) => event.stopPropagation()} :
        {}),
      [isFlyoutGalleryLayoutActive]
    );

    /** KL-1087: nested scroll on LHS gallery only while desktop flyout is open (PM: least design deviation). */
    const galleryStickyTopProps = useMemo(() => {
      const stickyTopLg = isHeaderHidden ? '48px' : 'calc(var(--header-height, 0px) + 48px)';

      return {
        top: {lg: stickyTopLg},
        ...(isFlyoutGalleryLayoutActive ?
          {
            maxHeight: {lg: 'calc(100dvh - var(--pdp-gallery-sticky-top))'},
            overflowY: {lg: 'auto'},
            overscrollBehavior: {lg: 'contain'},
            sx: {
              '--pdp-gallery-sticky-top': stickyTopLg,
              touchAction: {lg: 'pan-y'},
              WebkitOverflowScrolling: {lg: 'touch'},
            },
          } :
          {
            sx: {'--pdp-gallery-sticky-top': stickyTopLg},
          }),
      };
    }, [isFlyoutGalleryLayoutActive, isHeaderHidden]);

    const isFinishedGoodSimplePdp = isSimpleProduct && isFinishedGood;

    /** Shared props for the single ConfigurationSelector instance. */
    const configurationSelectorProps = useMemo(
      () => ({
        chooseLayoutFooter: showChooseYourLayoutTrigger ? productPromotionAccordion : null,
        configProducts: isFinishedGoodSimplePdp && finishedGoodMasterProduct ?
          [finishedGoodMasterProduct] :
          configProducts,
        coverGroups,
        headerTitle: intl.formatMessage(
          isFinishedGoodSimplePdp ?
            {
              defaultMessage: 'Layout',
              id: 'configuration_selector.section.title.layout',
            } :
            {
              defaultMessage: 'Choose your layout',
              id: 'configuration_selector.drawer.title',
            }
        ),
        isFinishedGoodSimplePdp,
        isLoading: isFinishedGoodSimplePdp ?
          (isFinishedGoodCoverVariantLoading || isFinishedGoodMasterLoading) :
          isConfigLoading,
        isOpen: isConfigOpen,
        onClose: handleConfigClose,
        onOpen: handleOpenChooseLayout,
        onSelect: setSelectedConfigId,
        panelMode: configPanelMode,
        product: layoutProduct,
        selectedId: selectedConfigId,
        variationParams,
      }),
      [
        configProducts,
        finishedGoodMasterProduct,
        coverGroups,
        configPanelMode,
        handleConfigClose,
        handleOpenChooseLayout,
        intl,
        isConfigLoading,
        isConfigOpen,
        isFinishedGoodCoverVariantLoading,
        isFinishedGoodMasterLoading,
        isFinishedGoodSimplePdp,
        layoutProduct,
        productPromotionAccordion,
        selectedConfigId,
        showChooseYourLayoutTrigger,
        variationParams
      ]
    );

    const priceData = useMemo(() => {
      const base = getPriceData(product, {quantity});

      // getPriceData is price-book only (sale book vs RRP) and ignores promotions.
      // Complex PDPs always show the after-promotion price; mirror that for Simple PDPs
      // by overlaying the effective (lowest productPromotions) price when it beats the
      // sale-book price. The strike-through "was" becomes the RRP when one exists, else
      // the sale-book price, so saving = was − promo. See docs/product/pricing-architecture.md §3a.
      const effective = getProductEffectivePrice(product);

      if (effective != null && base?.currentPrice != null && effective < base.currentPrice) {
        const wasPrice = base.listPrice ?? base.currentPrice;

        return {
          ...base,
          currentPrice: effective,
          listPrice: wasPrice > effective ? wasPrice : null,
          isOnSale: wasPrice > effective,
        };
      }

      return base;
    }, [product, quantity]);

    // Build STEPS for simple PDP configurable options
    const STEPS = useMemo(() => {
      return hasConfigurableOptions ? buildSteps(product, {isSimplePDP: true}) : [];
    }, [hasConfigurableOptions, product]);

    const isCustomizeComplete = useMemo(() => {
      if (isFinishedGood || !hasConfigurableOptions) return true;

      return isCustomizeJourneyComplete(committedSelection?.selections, STEPS, {
        coverGroups: getCoverGroups(product),
      });
    }, [isFinishedGood, hasConfigurableOptions, STEPS, committedSelection?.selections, product]);

    // Unified "uses CustomizeJourneyContext for pricing/gallery" predicate — true for
    // Simple+configurable options (KINDW-1119) AND Simple Freeform (KINDW-1121).
    // Finished Goods (TSD §7) opt out: the SKU price is already inclusive of all
    // recommendation prices, so summing per-selection prices would double-count (or
    // worse, return zero because we never commit selections in readOnly).
    const useCustomizePricing = !isFinishedGood && (hasConfigurableOptions || isSimpleFreeForm);

    // Cover variant for the currently committed selection (Freeform/CYO).
    // Drives the KingGuard source resolution below — without it, the headline
    // "Total price" silently drops the per-variant KingGuard price on Simple
    // PDPs (master's option model misses the variant overrides). See
    // `resolveKingGuardSourceProduct` for the rationale.
    const customizeTotalCoverVariant = useCommittedCoverVariantProduct(product);
    const customizeKingGuardSource = useMemo(
      () => resolveKingGuardSourceProduct(product, customizeTotalCoverVariant),
      [product, customizeTotalCoverVariant]
    );

    const customizeTotal = useMemo(() => {
      if (!useCustomizePricing) return priceData;

      // KINDW-1595 — Configurable Master shows variation-base min retail/sale from
      // `c_priceProcessed` until the shopper selects a cover (no preselect on load).
      if (isSimpleConfigurableMaster && !committedSelection?.selections?.cover?.productId) {
        return priceData;
      }

      const totalPrice = computeCustomizeTotal(committedSelection?.selections, product, true, customizeKingGuardSource);
      const listTotalPrice = computeCustomizeListTotal(committedSelection?.selections, product, true, customizeKingGuardSource);

      return {
        ...priceData,
        currentPrice: totalPrice,
        listPrice: listTotalPrice,
        isOnSale: totalPrice < listTotalPrice,
      };
    }, [
      useCustomizePricing,
      isSimpleConfigurableMaster,
      committedSelection?.selections,
      product,
      priceData,
      customizeKingGuardSource
    ]);

    /** Same display total as AddToCartStrip — passed to cover overflow flyout footer only (KINDW-1749). */
    const coverFlyoutAtcDisplayPrice = useMemo(() => {
      const complexTotal = computeCustomizeTotal(
        committedSelection?.selections,
        product,
        true,
        customizeKingGuardSource,
        setOptionPriceMap
      );

      return resolveCustomizeAtcDisplayPrice({
        complexTotal,
        masterProductPrice: product?.price ?? 0,
      });
    }, [committedSelection?.selections, customizeKingGuardSource, product, setOptionPriceMap]);

    const complexPdpSaleTotal = resolveCustomizeAtcDisplayPrice({
      complexTotal: computeCustomizeTotal(committedSelection?.selections, customizeTotalCoverVariant),
      masterProductPrice: product?.price ?? 0,
    });

    const settledFlowHydrateOptionId = useMemo(() => {
      if (!isSimplePdpSettledGalleryFlow) return null;
      if (simplePdpHeroDriver !== 'option') return null;
      if (simplePdpOptionPreviewProduct) return null;

      return resolveMostRecentCommittedOptionProductId(committedSelection?.selections, STEPS);
    }, [
      committedSelection?.selections,
      isSimplePdpSettledGalleryFlow,
      simplePdpHeroDriver,
      simplePdpOptionPreviewProduct,
      STEPS
    ]);

    const {data: settledFlowHydrateOptionResult} = useProducts(
      {parameters: {ids: settledFlowHydrateOptionId ?? '', ...GET_PRODUCTS_PARAMS}},
      {
        enabled: Boolean(settledFlowHydrateOptionId),
        keepPreviousData: false,
      }
    );

    const settledFlowHydrateOptionProduct = useMemo(() => {
      if (!settledFlowHydrateOptionId) return null;

      const fetched = settledFlowHydrateOptionResult?.data?.[0];

      if (!fetched) return null;

      const fetchedId = fetched.id ?? fetched.productId;

      return fetchedId != null && String(fetchedId) === String(settledFlowHydrateOptionId) ?
        fetched :
        null;
    }, [settledFlowHydrateOptionId, settledFlowHydrateOptionResult]);

    const effectiveSimplePdpOptionPreviewProduct =
      simplePdpOptionPreviewProduct ?? settledFlowHydrateOptionProduct;

    effectiveSimplePdpOptionPreviewRef.current = effectiveSimplePdpOptionPreviewProduct;

    const simplePdpMainGalleryDraft = useMemo(() => {
      if (!isSimplePdpSettledGalleryFlow) return null;

      return resolveSimplePdpMainGalleryState({
        addOnDetailsProduct,
        committedSelections: committedSelection?.selections,
        coverVariantProduct: galleryCoverScapiProduct,
        coverVariationValues: galleryCoverVariationValues,
        heroDriver: galleryHeroDriver,
        masterProduct: product,
        optionPreviewProduct: effectiveSimplePdpOptionPreviewProduct,
        selectedCoverId: gallerySelectedCoverId,
        useCustomizePricing,
      });
    }, [
      addOnDetailsProduct,
      committedSelection?.selections,
      effectiveSimplePdpOptionPreviewProduct,
      galleryCoverScapiProduct,
      galleryCoverVariationValues,
      galleryHeroDriver,
      gallerySelectedCoverId,
      isSimplePdpSettledGalleryFlow,
      product,
      useCustomizePricing
    ]);

    useLayoutEffect(() => {
      if (simplePdpMainGalleryDraft?.settled) {
        lastSettledSimplePdpGalleryRef.current = simplePdpMainGalleryDraft;
      }
    }, [simplePdpMainGalleryDraft]);

    const simplePdpMainGalleryDisplay = useMemo(() => {
      if (!simplePdpMainGalleryDraft) return null;

      if (simplePdpMainGalleryDraft.settled) {
        return simplePdpMainGalleryDraft;
      }

      // Cover last-touch: draft uses layers=[] — never flash a prior option's video while
      // the cover variant SCAPI fetch settles (keepPreviousData: false).
      if (galleryHeroDriver === 'cover') {
        return simplePdpMainGalleryDraft;
      }

      return lastSettledSimplePdpGalleryRef.current ?? simplePdpMainGalleryDraft;
    }, [galleryHeroDriver, simplePdpMainGalleryDraft]);

    useEffect(() => {
      if (!isSimplePdpSettledGalleryFlow) return;
      if (hasHydratedSimplePdpGalleryDriverRef.current) return;
      if (!hasCommittedSelectionData(committedSelection?.selections)) return;
      if (addOnDetailsProduct) return;

      const driver = resolveSimplePdpHeroDriverFromCommittedSelections(
        committedSelection?.selections,
        STEPS
      );

      if (driver === 'cover') {
        applySimplePdpGalleryDriver('cover');
      } else if (driver === 'option') {
        applySimplePdpGalleryDriver('option');
      }

      hasHydratedSimplePdpGalleryDriverRef.current = true;
    }, [
      addOnDetailsProduct,
      applySimplePdpGalleryDriver,
      committedSelection?.selections,
      isSimplePdpSettledGalleryFlow,
      STEPS
    ]);

    const galleryProduct = hasConfigurableOptions ? (inlineGalleryMirror.product ?? product) : product;
    const galleryVariationAttributes = useMemo(() => {
      if (isSimplePdpSettledGalleryFlow) {
        return simplePdpMainGalleryDisplay.galleryVariationAttributes;
      }

      if (isSimpleMasterWithCovers && freeformCoverSelection?.variationValues) {
        return freeformCoverSelection.variationValues;
      }

      return variationParams;
    }, [
      freeformCoverSelection?.variationValues,
      isSimpleMasterWithCovers,
      isSimplePdpSettledGalleryFlow,
      simplePdpMainGalleryDisplay,
      variationParams
    ]);

    const simplePdpGalleryLayers = useMemo(() => {
      if (isSimplePdpSettledGalleryFlow) {
        return simplePdpMainGalleryDisplay.layers;
      }

      return useCustomizePricing ?
        buildLayersFromSelections(committedSelection?.selections) :
        [];
    }, [
      committedSelection?.selections,
      isSimplePdpSettledGalleryFlow,
      simplePdpMainGalleryDisplay,
      useCustomizePricing
    ]);

    const inlineCustomizeSessionProps = useMemo(
      () => ({
        autoCommit: true,
        closePickerRef: closeInlinePickerRef,
        ...(isSimplePdpSettledGalleryFlow ?
          {
            committedOptionProduct: effectiveSimplePdpOptionPreviewProduct,
            coverVariantProduct: galleryCoverScapiProduct,
          } :
          {}),
        mobileGalleryLayers: simplePdpGalleryLayers,
        mobileGalleryPreviewProduct: simplePdpMainGalleryDisplay?.previewProduct,
        mobileGalleryVariationAttributes: galleryVariationAttributes,
        product,
        onCoverCommitted: handleSimplePdpInlineCoverCommitted,
        onCoverPickerOpen: handleSimplePdpInlineCoverCommitted,
        onPickerOpenChange: setIsInlineCustomizePickerOpen,
        onSlotProductPreview: handleSimplePdpSlotProductPreview,
        onViewDetails: handleSimplePdpViewDetails,
      }),
      [
        effectiveSimplePdpOptionPreviewProduct,
        galleryCoverScapiProduct,
        galleryVariationAttributes,
        handleSimplePdpInlineCoverCommitted,
        handleSimplePdpSlotProductPreview,
        handleSimplePdpViewDetails,
        isSimplePdpSettledGalleryFlow,
        product,
        simplePdpGalleryLayers,
        simplePdpMainGalleryDisplay
      ]
    );

    const seeDetailsGalleryResourceOverride = useMemo(() => {
      if (!seeDetailsOverlayProduct) return null;

      const overlayVariationAttributes =
        seeDetailsOverlayProduct._selectedVariationValues ??
        seeDetailsOverlayProduct.variationValues ??
        galleryVariationAttributes;

      return resolveViewDetailsOptionGalleryResource(
        seeDetailsOverlayProduct,
        product,
        overlayVariationAttributes
      );
    }, [seeDetailsOverlayProduct, product, galleryVariationAttributes]);

    // Unified gallery resource for ImageGalleryV2 — predefined set and customize-your-own
    // use the same committed-selection resolver as set cards, cart, and upsell.
    const committedGallerySelections =
      isComplexProduct &&
      committedSelection?.selections &&
      (committedSelection.mode === 'set' || committedSelection.mode === 'customize') ?
        committedSelection.selections :
        null;

    const activeGalleryResource = useMemo(() => {
      if (isDesktop && addOnDetailsProduct) {
        return resolveGalleryResourceForProduct(addOnDetailsProduct);
      }

      if (committedGallerySelections) {
        return resolveCommittedSelectionGalleryResource(
          product,
          committedGallerySelections,
          {allowCylindo: true}
        );
      }

      return resolveGalleryResource({
        allowCylindo: !isSimpleProduct,
        layers: buildLayersFromSelections(committedGallerySelections),
        masterProduct: galleryProduct,
        previewProduct: inlineGalleryMirror.product,
        selections: committedSelection?.selections,
        selectedVariationAttributes: galleryVariationAttributes,
      });
    }, [
      isDesktop,
      addOnDetailsProduct,
      product,
      committedGallerySelections,
      galleryProduct,
      galleryVariationAttributes,
      inlineGalleryMirror.product,
      committedSelection?.selections,
      isSimpleProduct
    ]);

    // Simple PDP ViewDetails under main gallery (desktop). Cover grid may sit outside
    // CustomizeStepsInline, so mirror can be empty while a cover is already committed.
    const simplePdpReviewDetailsProduct = useMemo(
      () => resolveSimplePdpMainViewDetailsProduct({
        addOnDetailsProduct,
        committedCoverProduct: isSimplePdpSettledGalleryFlow ?
          galleryCoverScapiProduct :
          (isSimpleMasterWithCovers ? customizeTotalCoverVariant : null),
        hasConfigurableOptions,
        masterProduct: galleryProduct,
        mirrorViewDetailsProduct: isSimplePdpSettledGalleryFlow ?
          simplePdpMainGalleryDisplay?.viewDetailsProduct :
          inlineGalleryMirror.viewDetailsProduct,
      }),
      [
        addOnDetailsProduct,
        customizeTotalCoverVariant,
        galleryCoverScapiProduct,
        galleryProduct,
        hasConfigurableOptions,
        inlineGalleryMirror.viewDetailsProduct,
        isSimpleMasterWithCovers,
        isSimplePdpSettledGalleryFlow,
        simplePdpMainGalleryDisplay
      ]
    );

    const showSimplePdpGallerySummary = useMemo(
      () => resolveSimplePdpShowGallerySummary({
        addOnDetailsProduct,
        committedSelections: committedSelection?.selections,
        hasConfigurableOptions,
        isInlineCustomizePickerOpen,
        isSimpleMasterWithCovers,
        masterProductId: product?.id ?? product?.productId ?? null,
        previewVariationValues: inlineGalleryMirror.selectedVariationAttributes,
        viewDetailsProduct: simplePdpReviewDetailsProduct,
      }),
      [
        addOnDetailsProduct,
        committedSelection?.selections,
        hasConfigurableOptions,
        inlineGalleryMirror.selectedVariationAttributes,
        isInlineCustomizePickerOpen,
        isSimpleMasterWithCovers,
        product?.id,
        product?.productId,
        simplePdpReviewDetailsProduct
      ]
    );

    const isProductASet = product?.type.set;
    const isProductABundle = product?.type.bundle;
    const showInPageAtcOnMobile = !isProductPartOfBundle;

    const shouldShowInformationTabs = useMemo(() => {
      if (isComplexProduct) return true;

      const hasVariationProducts = product?.type === 'master' || (product?.variants && product.variants.length > 0);
      const hasRecommendationProducts = hasConfigurableOptions;
      const hasProductOptions = product?.options && product.options.length > 0;

      return !isComplexProduct &&
        (hasVariationProducts || hasRecommendationProducts || hasProductOptions);
    }, [isComplexProduct, hasConfigurableOptions, product]);

    const hasVariationsSectionVariantContent = useMemo(() => {
      if (isProductPartOfBundle) return true;

      if (shouldShowInformationTabs) return true;

      if (!showDesktopConfigPanel && showChooseYourLayoutTrigger) return true;

      if (
        isFinishedGood &&
        (isProductPartOfSet || isProductPartOfBundle || !showChooseYourLayoutTrigger)
      ) {
        return true;
      }

      if (isFinishedGood) return true;
      if (isSimpleConfigurableMaster) return true;

      if (isSimpleFreeForm && freeformCoverVariants.length > 0) return true;

      if (isSimpleProduct && hasConfigurableOptions) return true;

      if (
        !isFinishedGood &&
        (isProductPartOfSet || isProductPartOfBundle || !showChooseYourLayoutTrigger) &&
        productPromotionAccordion
      ) {
        return true;
      }

      if (product && !isProductPartOfSet && !isProductPartOfBundle) {
        if (isComplexProduct) return true;

        const hasOptionalAddOns =
          product?.c_displayKingGuard === true ||
          getOptionalAddOns(product).some(
            (value) => filterVisibleOptionProducts(value?.products).length > 0
          );

        if (hasOptionalAddOns) return true;
        if (instockProducts?.length > 0) return true;
      }

      return false;
    }, [
      freeformCoverVariants.length,
      hasConfigurableOptions,
      instockProducts?.length,
      isComplexProduct,
      isFinishedGood,
      isProductPartOfBundle,
      isProductPartOfSet,
      isSimpleConfigurableMaster,
      isSimpleFreeForm,
      isSimpleProduct,
      product,
      productPromotionAccordion,
      shouldShowInformationTabs,
      showChooseYourLayoutTrigger,
      showDesktopConfigPanel,
    ]);

    const variationsSectionContentNoVariant = useMemo(
      () => Boolean(product && !isProductPartOfSet && !isProductPartOfBundle) &&
        !hasVariationsSectionVariantContent,
      [hasVariationsSectionVariantContent, isProductPartOfBundle, isProductPartOfSet, product]
    );

    const hasSpecificationsContent = useMemo(
      () => hasProductSpecificationsContent(product),
      [product]
    );

    const showSpecificationsAccordionInProductView = useMemo(() => {
      const embedInView = isComplexProduct &&
        ((!isProductASet && !isProductABundle) || isProductPartOfSet || isProductPartOfBundle);

      return embedInView || (isSimpleProduct && !isProductPartOfSet && !isProductPartOfBundle);
    }, [
      isComplexProduct,
      isProductASet,
      isProductABundle,
      isProductPartOfSet,
      isProductPartOfBundle,
      isSimpleProduct
    ]);

    // Complex + simple standalone PDPs: sync Build / Specifications tab highlight while scrolling.
    const shouldSyncTabsFromScroll =
      shouldShowInformationTabs &&
      showSpecificationsAccordionInProductView &&
      hasSpecificationsContent;

    const errorContainerRef = useRef(null);
    const primaryAddToCartButtonRef = useRef(null);
    const [inPageCtaElement, setInPageCtaElement] = useState(null);
    const assignPrimaryCtaRef = useCallback((node) => {
      primaryAddToCartButtonRef.current = node;
      setInPageCtaElement(node || atcSectionRef.current);
    }, []);
    const assignAtcSectionRef = useCallback((node) => {
      atcSectionRef.current = node;

      if (!primaryAddToCartButtonRef.current) {
        setInPageCtaElement(node);
      }
    }, []);
    const hideMobileStickyCta = useInPageCtaVisibility(inPageCtaElement);
    const scrollTargetRef = useRef(null);
    const atcSectionRef = useRef(null);
    const mainFlexContainerRef = useRef(null);
    const tabScrollSyncSuppressUntilRef = useRef(0);

    const assignScrollTargetRef = useCallback(
      (el) => {
        scrollTargetRef.current = el;
        if (typeof ref === 'function') {
          ref(el);
        } else if (ref) {
          ref.current = el;
        }
      },
      [ref]
    );

    const {disableButton} = useMemo(() => {
      let shouldDisableButton =
        showInventoryMessage || isAddToCartBlocked || isPdpInventoryLoading;
      let currentInventoryMsg = '';

      if (
        !shouldDisableButton &&
        (isProductASet || isProductABundle) &&
        childProductOrderability
      ) {
        // if any of the children are not orderable, it will disable the add to cart button
        const unavailableChildProductKey = Object.keys(childProductOrderability).find(
          (key) => {
            return childProductOrderability[key].showInventoryMessage;
          }
        );

        shouldDisableButton = !!unavailableChildProductKey;
        if (unavailableChildProductKey) {
          const unavailableChildProduct =
            childProductOrderability[unavailableChildProductKey];

          if (unavailableChildProduct.unfulfillable) {
            currentInventoryMsg = intl.formatMessage(
              {
                defaultMessage: 'Only {stockLevel} left for {productName}!',
                id: 'use_product.message.inventory_remaining_for_product',
              },
              {
                stockLevel: unavailableChildProduct.stockLevel,
                productName: unavailableChildProduct.productName,
              }
            );
          }
          if (unavailableChildProduct.isOutOfStock) {
            currentInventoryMsg = intl.formatMessage(
              {
                defaultMessage: 'Out of stock for {productName}',
                id: 'use_product.message.out_of_stock_for_product',
              },
              {productName: unavailableChildProduct.productName}
            );
          }
        }
      }

      return {disableButton: shouldDisableButton, customInventoryMessage: currentInventoryMsg};
    }, [showInventoryMessage, childProductOrderability, isAddToCartBlocked, isPdpInventoryLoading]);

    // Handle tab click for Build & Customise / Specifications tabs
    const handleTabClick = (tab) => {
      if (activeTab === tab) {
        return;
      }

      tabScrollSyncSuppressUntilRef.current = Date.now() + 1200;
      setActiveTab(tab);
      if (tab === 'specifications') {
        const specificationsSection = document.getElementById('specifications');

        scrollToWithHeaderOffset(specificationsSection, undefined, {recheckAfterLayout: true});
      } else if (tab === 'build') {
        const buildEl = scrollTargetRef.current ?? ref?.current;

        if (buildEl) {
          scrollToWithHeaderOffset(buildEl, undefined, {recheckAfterLayout: true});
        }
      }
    };

    // Keep tab highlight in sync with scroll position; listen to flyout inner scroll when applicable.
    useLayoutEffect(() => {
      if (!shouldSyncTabsFromScroll) {
        return undefined;
      }

      let raf = 0;
      const syncTabFromScrollPosition = () => {
        if (Date.now() < tabScrollSyncSuppressUntilRef.current) {
          return;
        }

        const specs = document.getElementById('specifications');
        const buildEl = scrollTargetRef.current;

        if (!specs?.isConnected || !buildEl?.isConnected) {
          return;
        }

        const threshold = getStickyHeaderHeightPx() + 32;
        const specsTop = specs.getBoundingClientRect().top;
        const nextTab = specsTop > threshold ? 'build' : 'specifications';

        setActiveTab((current) => (current === nextTab ? current : nextTab));
      };

      const onScroll = () => {
        if (raf) {
          return;
        }

        raf = window.requestAnimationFrame(() => {
          raf = 0;
          syncTabFromScrollPosition();
        });
      };

      window.addEventListener('scroll', onScroll, {passive: true});
      const mainEl = isFlyoutGalleryLayoutActive ? mainFlexContainerRef.current : null;

      mainEl?.addEventListener('scroll', onScroll, {passive: true});

      return () => {
        window.removeEventListener('scroll', onScroll);
        mainEl?.removeEventListener('scroll', onScroll);

        if (raf) {
          window.cancelAnimationFrame(raf);
        }
      };
    }, [shouldSyncTabsFromScroll, isFlyoutGalleryLayoutActive]);

    const validateAndShowError = (opts = {}) => {
      const {scrollErrorIntoView = true} = opts;
      // Validate that all attributes are selected before proceeding.
      const hasValidSelection = validateOrderability(variant, product, quantity, stockLevel);
      const showError = !isProductASet && !isProductABundle && !hasValidSelection;
      const scrollToError = showError && scrollErrorIntoView;

      toggleShowOptionsMessage(showError);

      if (scrollToError) {
        errorContainerRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }

      return hasValidSelection;
    };

    const handleCartItem = useCallback(async () => {
      const hasValidSelection = validateAndShowError();

      if (!hasValidSelection) {
        return null;
      }

      if (!addToCart && !updateCart) return null;

      if (updateCart) {
        await updateCart(variant || product, quantity);

        return;
      }

      try {
        const itemsAdded = await addToCart(variant, quantity);

        if (itemsAdded) {
          handlePostAddToCart({
            product,
            itemsAdded,
            selectedQuantity: quantity,
          });
        }
      } catch {
        showToast({title: intl.formatMessage(API_ERROR_MESSAGE), status: 'error'});
      }
    }, [validateAndShowError, addToCart, updateCart, variant, product, quantity, handlePostAddToCart, showToast, intl]);

    const renderActionButtons = ({primaryCtaRef} = {}) => {
      const buttons = [];
      const buttonText = {
        update: intl.formatMessage({
          defaultMessage: 'Update',
          id: 'product_view.button.update',
        }),
        addToCart: intl.formatMessage({
          defaultMessage: 'Add to cart',
          id: 'product_view.button.add_to_cart',
        }),
        addSetToCart: intl.formatMessage({
          defaultMessage: 'Add Set to Cart',
          id: 'product_view.button.add_set_to_cart',
        }),
        addBundleToCart: intl.formatMessage({
          defaultMessage: 'Add Bundle to Cart',
          id: 'product_view.button.add_bundle_to_cart',
        }),
        addToWishlist: intl.formatMessage({
          defaultMessage: 'Add to Wishlist',
          id: 'product_view.button.add_to_wishlist',
        }),
        addSetToWishlist: intl.formatMessage({
          defaultMessage: 'Add Set to Wishlist',
          id: 'product_view.button.add_set_to_wishlist',
        }),
        addBundleToWishlist: intl.formatMessage({
          defaultMessage: 'Add Bundle to Wishlist',
          id: 'product_view.button.add_bundle_to_wishlist',
        }),
      };

      const showError = () => {
        showToast({
          title: intl.formatMessage(API_ERROR_MESSAGE),
          status: 'error',
        });
      };

      const handleCartItem = async () => {
        if (isAddToCartBlocked || isPdpInventoryLoading) {
          return null;
        }

        const hasValidSelection = validateAndShowError();

        if (!hasValidSelection) {
          return null;
        }

        if (!addToCart && !updateCart) return null;
        if (updateCart) {
          await updateCart(variant || product, quantity);

          return;
        }
        try {
          const itemsAdded = await addToCart(variant, quantity);

          // Open modal only when `addToCart` returns some data
          // It's possible that the item has been added to cart, but we don't want to open the modal.
          // See wishlist_primary_action for example.
          if (itemsAdded) {
            handlePostAddToCart({
              product,
              itemsAdded,
              selectedQuantity: quantity,
            });
          }
        } catch (e) {
          showError();
        }
      };

      // const handleWishlistItem = async () => {
      //   if (!updateWishlist && !addToWishlist) return null;
      //   if (updateWishlist) {
      //     updateWishlist(product, variant, quantity);

      //     return;
      //   }
      //   addToWishlist(product, variant, quantity);
      // };

      // REVIEW - This logic need to review again due to we have specific logic for type of product
      if (effectiveShowNotifyMe && isSimplePlainProduct && !isProductPartOfBundle) {
        buttons.push(
          <Button
            key="notify-me-button"
            {...styles.notifyMeButton}
            variant="filled-light"
            onClick={onNotifyMeClick}
          >
            <FormattedMessage
              defaultMessage="Notify me"
              id="product_view.button.notify_me"
            />
          </Button>
        );
      } else if ((addToCart || updateCart || addCustomizeToCart) && !isProductPartOfBundle) {
        // child product of bundles do not have add to cart button
        const currency = product?.currency || activeCurrency;

        // KINDW-1119: Simple PDPs with configurable options also need the
        // customize-aware ATC flow so option selections are added as child
        // line items and the price reflects the full configuration.
        // KINDW-1121: Simple Freeform uses the same ATC flow so the selected
        // cover variant becomes the primary line item.
        const useCustomizePath = (isComplexProduct || hasConfigurableOptions || isSimpleFreeForm) && addCustomizeToCart && !updateCart;

        if (effectiveShowNotifyMe && useCustomizePath) {
          // FSD §5.1 + §1.3 — OOS configuration renders Notify Me instead of Add to Cart.
          buttons.push(
            <Button
              key="cart-button"
              ref={primaryCtaRef}
              {...styles.actionButton}
              isDisabled={isCtaPending}
              isLoading={isCtaLoading}
              onClick={handleFreeformNotifyMeClick}
            >
              <FormattedMessage
                defaultMessage="Notify Me"
                id="product_view.button.notify_me"
              />
            </Button>
          );
        } else if (useCustomizePath) {
          buttons.push(
            <ComplexAddToCartButton
              key="cart-button"
              ref={primaryCtaRef}
              addCustomizeToCart={addCustomizeToCart}
              currency={currency}
              intl={intl}
              isAddToCartBlocked={isAddToCartBlocked || isPdpInventoryLoading}
              isBasketLoading={isBasketLoading}
              isCtaPending={isCtaPending}
              isSimplePdpFlow={!isComplexProduct && hasConfigurableOptions}
              product={product}
              styles={styles}
              onSuccess={handlePostAddToCart}
            />
          );
        } else if (effectiveShowNotifyMe) {
          buttons.push(
            <Button
              key="cart-button"
              ref={primaryCtaRef}
              {...styles.actionButton}
              onClick={handleFreeformNotifyMeClick}
            >
              <FormattedMessage
                defaultMessage="Notify Me"
                id="product_view.button.notify_me"
              />
            </Button>
          );
        } else {
          const formattedPrice = priceData?.currentPrice ?
            formatCurrencyHideTrailingZeroCents(intl, priceData.currentPrice, currency) :
            '';

          const buttonTextWithPrice = updateCart ?
            buttonText.update :
            isProductASet ?
              buttonText.addSetToCart :
              isProductABundle ?
                buttonText.addBundleToCart :
                `${buttonText.addToCart}${formattedPrice ? ` - ${formattedPrice}` : ''}`;

          buttons.push(
            <Button
              key="cart-button"
              ref={primaryCtaRef}
              {...styles.actionButton}
              isDisabled={disableButton}
              isLoading={isBasketLoading}
              onClick={handleCartItem}
            >
              {buttonTextWithPrice}
            </Button>
          );
        }
      }

      return buttons;
    };

    // Bind the reference with our `scope` that includes the internal validate function for this component.
    // Other values can be added to this scope as required.
    if (typeof ref === 'function') {
      ref = ref.bind({validateOrderability: validateAndShowError});
    }

    // Set the quantity of bundle child in a product bundle to ensure availability messages appear
    if (
      isProductPartOfBundle &&
      quantity != selectedBundleParentQuantity * childOfBundleQuantity
    ) {
      setQuantity(selectedBundleParentQuantity * childOfBundleQuantity);
    }

    useEffect(() => {
      if (isAddToCartModalOpen) {
        onAddToCartModalClose();
      }
    }, [location.pathname]);

    // Reset UI state when navigating between different products
    useEffect(() => {
      toggleShowOptionsMessage(false);
      setAddOnDetailsProduct(null);
    }, [product?.id]);

    // Clear add-on "See details" when the committed selection changes (e.g. user picks a
    // different predefined-set or completes a customize journey). The add-on detail view
    // belongs to a specific selection context — if the selection moves on, it should dismiss.
    useEffect(() => {
      setAddOnDetailsProduct(null);
      setSimplePdpHeroDriver((prev) => (prev === 'add-on' ? 'standing' : prev));
    }, [committedSelection?.mode, committedSelection?.productId]);

    useEffect(() => {
      if (
        !isProductASet &&
        !isProductABundle &&
        validateOrderability(variant, product, quantity, stockLevel)
      ) {
        toggleShowOptionsMessage(false);
      }
    }, [variationParams]);

    useEffect(() => {
      if (variant) {
        onVariantSelected(product, variant, quantity);
      }
    }, [variant?.productId, quantity]);

    useEffect(() => {
      if (isProductPartOfBundle || isProductPartOfSet) {
        const key = product.itemId ?? product.id;

        // when showInventoryMessage is true, it means child product is not orderable
        setChildProductOrderability((previousState) => ({
          ...previousState,
          [key]: {
            showInventoryMessage,
            isOutOfStock,
            unfulfillable,
            stockLevel,
            productName: product?.name,
          },
        }));
      }
    }, [showInventoryMessage, inventoryMessage]);

    const content = (
      <>
        <Flex
          ref={assignScrollTargetRef}
          className={isComplexProduct ? 'product-view product-view--complex' : 'product-view product-view--simple'}
          data-product-kind={isComplexProduct ? 'complex' : 'simple'}
          data-product-type={simpleProductType}
          data-testid="product-view"
          direction="column"
        >
          <Flex
            ref={mainFlexContainerRef}
            className={[
              'product-view__main-flex-container',
              variationsSectionContentNoVariant && 'product-view__main-flex-container-no-variant',
            ].filter(Boolean).join(' ')}
            {...styles.mainFlexContainer}
            {...styles.productView}
          >
            {/* Mobile: Base info above gallery */}
            <Box {...styles.mobileBaseInfoSection}>
              <BaseInformationSection
                category={category}
                hideChooseYourLayout={Boolean(product?.c_pimlyHideChooseYourLayout)}
                isSimpleProduct={isSimpleProduct}
                product={product}
                simpleProductType={simpleProductType}
              />
            </Box>

            {showImageGallery && (
              <Box
                className="product-view__gallery-container"
                order={2}
                {...styles.galleryContainer}
                {...galleryStickyTopProps}
                {...desktopFlyoutInteractiveProps}
                sx={{
                  ...(styles.galleryContainer?.sx || {}),
                  ...(galleryStickyTopProps?.sx || {}),
                }}
              >
                {product ? (
                  <>
                    {showDesktopConfigPanel ? (
                      // Desktop panel open: ConfigurationSelector renders DesktopShell
                      // (ReviewSection left + DesktopPanelView right as a Flex row).
                      <ConfigurationSelector
                        className="configuration-selector-component"
                        {...configurationSelectorProps}
                      />
                    ) : isSimpleProduct && (hasConfigurableOptions || isSimpleFreeForm) ? (
                      // Simple PDP: same ReviewSection as Complex flyout (Cylindo layers + ViewDetails).
                      // key remounts gallery when master id changes — avoids stale cylindo / React Query state.
                      // ViewDetails (additional info) renders below the gallery on DESKTOP only — mobile
                      // surfaces it via the separate "See Details" overlay (TSD §1.5 / §4.2).
                      <ReviewSection
                        key={product?.id ?? product?.productId}
                        galleryResourceOverride={addOnDetailsProduct ? activeGalleryResource : undefined}
                        layers={simplePdpGalleryLayers}
                        masterProduct={product}
                        previewProduct={addOnDetailsProduct ?? undefined}
                        selectedVariationAttributes={galleryVariationAttributes}
                        selections={committedSelection?.selections}
                        showViewDetailsSection={isDesktop && showSimplePdpGallerySummary}
                        viewDetailsProduct={simplePdpReviewDetailsProduct}
                        forceCylindoCheck
                        imageZoomFullScreen
                        isSimpleProduct
                      />
                    ) : (
                      <>
                        <ImageGalleryV2
                          key={(addOnDetailsProduct ?? galleryProduct)?.id + (committedSelection?.mode === 'set' ? (committedSelection?.productId ?? '') : '')}
                          customerId={activeGalleryResource.customerId}
                          cylindoFeatures={activeGalleryResource.cylindoFeatures}
                          cylindoId={activeGalleryResource.cylindoId}
                          lazy={!addOnDetailsProduct && (isProductPartOfSet || isProductPartOfBundle)}
                          pimImages={activeGalleryResource.pimImages}
                          productName={(addOnDetailsProduct ?? galleryProduct)?.name}
                          showArButton={!addOnDetailsProduct && !isSimpleProduct}
                          size={imageSize}
                        />

                        {!addOnDetailsProduct && showFullLink && getProductUrl(product) && (
                          <HideOnMobile>
                            <Link
                              {...styles.fullDetailsLink}
                              to={getProductUrl(product)}
                            >
                              <FormattedMessage
                                defaultMessage="See full details"
                                id="product_view.link.full_details"
                              />
                            </Link>
                          </HideOnMobile>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <ImageGallerySkeleton />
                )}

                <Box className="product-view__below-gallery-section" {...styles.belowGallerySection}>
                  {isDesktop && addOnDetailsProduct && !isConfigOpen &&
                    !showDesktopConfigPanel &&
                    !(isSimpleProduct && (hasConfigurableOptions || isSimpleFreeForm)) && (
                    <ViewDetails
                      description={addOnDetailsProduct.shortDescription}
                      longDescription={addOnDetailsProduct.longDescription}
                      title={addOnDetailsProduct.name}
                      thumbnail={
                        Array.isArray(addOnDetailsProduct.c_pimlyConfigurationImage) ?
                          addOnDetailsProduct.c_pimlyConfigurationImage :
                          []
                      }
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* Variations & Quantity Selector & CTA buttons — frozen when desktop flyout open (click closes flyout) */}
            <InlineCustomizeShell
              desktopPicker={<CustomizeStepsInlineDesktopPicker />}
              enabled={showInlineCustomizeShell}
              sessionProps={inlineCustomizeSessionProps}
            >
              <VStack
                {...styles.variationsSection}
                className="variations-section"
                {...desktopFlyoutFrozenProps}
              >
                {/* Desktop: Base info in right section */}
                <Box className="desktop-base-info-section" {...styles.desktopBaseInfoSection}>
                  <BaseInformationSection
                    category={category}
                    hideChooseYourLayout={Boolean(product?.c_pimlyHideChooseYourLayout)}
                    isSimpleProduct={isSimpleProduct}
                    product={product}
                    simpleProductType={simpleProductType}
                  />
                </Box>
                <VStack
                  align="stretch"
                  className={[
                    'variations-section-content',
                    variationsSectionContentNoVariant && 'variations-section-content-no-variant',
                  ].filter(Boolean).join(' ')}
                  spacing={4}
                >
                  {isProductPartOfBundle && (
                    <Box>
                      <Text aria-label="price" {...styles.quantityLabel}>
                        <label>
                          {intl.formatMessage({
                            defaultMessage: 'Quantity',
                            id: 'product_view.label.quantity',
                          })}
                          : {childOfBundleQuantity}
                        </label>
                      </Text>
                    </Box>
                  )}

                  {/* Information Tabs - Build & Customise and Specifications - data-driven visibility */}
                  {shouldShowInformationTabs && (
                    <Box className="product-tabs-container" {...styles.tabsContainer}>
                      <Box className="product-tabs-information-container" {...styles.productTabsInformationContainer}>
                        <Box
                          {...styles.productTabsInformationTab}
                          {...styles[activeTab === 'build' ? 'productTabsInformationTabActive' : 'productTabsInformationTabInactive']}
                          role="button"
                          tabIndex={0}
                          onClick={() => handleTabClick('build')}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              handleTabClick('build');
                            }
                          }}
                        >
                          <Text
                            {...styles.productTabsInformationTabText}
                            transition="color 0.2s ease"
                          >
                            {intl.formatMessage(
                              isSimpleProduct && isFinishedGood ?
                                {
                                  defaultMessage: 'Package details',
                                  id: 'product_detail.tab.title.package_details',
                                } :
                                {
                                  defaultMessage: 'Build & Customise',
                                  id: 'product_detail.tab.title.build_and_customise',
                                }
                            )}
                          </Text>
                        </Box>
                        <Box
                          aria-hidden={!hasSpecificationsContent}
                          tabIndex={hasSpecificationsContent ? 0 : -1}
                          className={
                            hasSpecificationsContent ?
                              'product-tabs-information-tab product-tabs-information-tab--specifications' :
                              'product-tabs-information-tab product-tabs-information-tab--specifications product-tabs-information-tab--hidden'
                          }
                          {...styles.productTabsInformationTab}
                          {...(!hasSpecificationsContent ? styles.productTabsInformationTabHidden : {})}
                          {...styles[activeTab === 'specifications' ? 'productTabsInformationTabActive' : 'productTabsInformationTabInactive']}
                          role="button"
                          onClick={() => {
                            if (hasSpecificationsContent) {
                              handleTabClick('specifications');
                            }
                          }}
                          onKeyDown={(e) => {
                            if (hasSpecificationsContent && (e.key === 'Enter' || e.key === ' ')) {
                              handleTabClick('specifications');
                            }
                          }}
                        >
                          <Text
                            {...styles.productTabsInformationTabText}
                            transition="color 0.2s ease"
                          >
                            {intl.formatMessage({
                              defaultMessage: 'Specifications',
                              id: 'product_detail.tab.title.specifications',
                            })}
                          </Text>
                        </Box>
                      </Box>
                    </Box>
                  )}

                  {/* ConfigurationSelector - layout trigger and/or Complex mobile customize drawer */}
                  {!showDesktopConfigPanel &&
                    (showChooseYourLayoutTrigger ||
                      (isComplexProduct && !isProductPartOfSet && !isProductPartOfBundle)) && (
                    <ConfigurationSelector
                      className="configuration-selector-component"
                      {...configurationSelectorProps}
                    />
                  )}

                  {/* Finished goods: promotion sits above FinishedGoodView (sibling, not inside). */}
                  {isFinishedGood &&
                    (isProductPartOfSet || isProductPartOfBundle || !showChooseYourLayoutTrigger) && (
                    <Box className="finished-good-promotion" {...styles.finishedGoodPromotion}>
                      {productPromotionAccordion}
                    </Box>
                  )}

                  {/* TSD §7 — Finished Goods (e.g. Jasper / 1977). Pre-bundled simple SKU:
                    cover variant + option recommendations are baked in (no picking). The
                    finished-good view replaces BOTH the freeform cover swatch grid AND the
                    interactive customize steps with a static "what's included" list +
                    Contentful "Not your perfect match" Customize link to the master PDP.
                    `<ConfigurationSelector>` (rendered above) still drives the "Layout"
                    section because finished goods sit under the variation-base master
                    just like freeform variants. */}
                  {isFinishedGood ? (
                    <FinishedGoodView masterProduct={finishedGoodMasterProduct} product={product} />
                  ) : isSimpleConfigurableMaster ? (
                  // KINDW-1124 / FSD §SECTION-PDP-SIMPLE-08 — Configurable Master.
                  // Variation-base master with required recommendation containers but NO linked
                  // products. The slot composes the freeform cover swatch grid (KINDW-1121) and
                  // the inline customize steps (KINDW-1119) so the right column body reads as a
                  // single render call. The mobile "See Details" overlay below is shared with all
                  // simple PDP types, so it stays out here.
                    <ConfigurableMasterView
                      coverMasterMinPrice={coverMasterMinPrice}
                      coverVariants={freeformCoverVariants}
                      currency={product?.currency || activeCurrency}
                      product={product}
                      selectedCoverId={committedCoverProductId}
                      onCoverMoreClick={handleCoverOverflowOpen}
                      onCoverSelect={handleFreeformCoverSelect}
                      onViewDetails={handleCoverViewDetails}
                    />
                  ) : (
                    <>
                      {/* KINDW-1121 / FSD §5 — Freeform-style cover swatch grid.
                        4×2 swatches with inline OOS overlay / stock messaging; overflow (>8)
                        is absorbed by a "+N More" CTA that opens an overflow drawer.
                        Renders for Simple Freeform here; the Configurable Master case is
                        delegated to <ConfigurableMasterView> above (which also includes the
                        cover grid). steps-main-view.jsx skips the cover step when
                        isSimplePDP=true so the inline cover slot card never double-renders. */}
                      {isSimpleFreeForm && freeformCoverVariants.length > 0 && (
                        <CoverSwatchGrid
                          currency={product?.currency || activeCurrency}
                          masterMinPrice={coverMasterMinPrice}
                          selectedCoverId={committedCoverProductId}
                          variants={freeformCoverVariants}
                          onMoreClick={handleCoverOverflowOpen}
                          onSelect={handleFreeformCoverSelect}
                          onViewDetails={handleCoverViewDetails}
                        />
                      )}

                      {/* Simple PDP with configurable options - inline customization steps.
                        Configurable Master is excluded here because <ConfigurableMasterView>
                        already renders <CustomizeStepsInline> for that case. Color/Size +
                        Multi-step still render through this branch. */}
                      {isSimpleProduct && hasConfigurableOptions && (
                        <CustomizeStepsInline />
                      )}
                    </>
                  )}

                  {/* Simple PDP "See Details" mobile overlay — same component (and gallery layering)
                    as the Complex flyout's mobile asOverlay. Shared logic: any change here updates
                    both Simple and Complex PDPs. (KINDW-1119 §B2) */}
                  {isSimpleProduct && seeDetailsOverlayProduct && (
                    <ReviewSection
                      galleryResourceOverride={seeDetailsGalleryResourceOverride}
                      layers={seeDetailsGalleryResourceOverride ? undefined : simplePdpGalleryLayers}
                      masterProduct={product}
                      overlayCoverItemType={seeDetailsOverlayCoverItemType ?? undefined}
                      overlayDynamicTitleFromShortDescription={!seeDetailsOverlayCoverItemType}
                      previewProduct={seeDetailsOverlayProduct}
                      selectedVariationAttributes={galleryVariationAttributes}
                      selections={committedSelection?.selections}
                      viewDetailsProduct={seeDetailsOverlayProduct}
                      asOverlay
                      forceCylindoCheck
                      isOpen
                      isSimpleProduct
                      onClose={handleSimplePdpViewDetailsClose}
                    />
                  )}

                  {/* Promotion below customize blocks only when there is no Choose your layout section */}
                  {!isFinishedGood &&
                    (isProductPartOfSet || isProductPartOfBundle || !showChooseYourLayoutTrigger) &&
                    productPromotionAccordion}

                  {/* Product Customize - Choose your layout before popular sets (design); panel instance lives in gallery when flyout open */}
                  {product && !isProductPartOfSet && !isProductPartOfBundle && (
                    <>
                      {/* ChooseYourSets - Complex products only (flyout/guided experience) */}
                      {isComplexProduct && (
                        <ChooseYourSets isComplexProduct={isComplexProduct} product={product} onOpenCustomize={handleOpenCustomize} />
                      )}

                      {/* Optional add-ons - above delivery availability (PDP layout) */}
                      <OptionalAddOnsContainer
                        kingGuardSlot={product?.c_displayKingGuard === true ? <KingGuardSlot product={product} /> : null}
                        product={product}
                        onAddOnDetailsOpen={handleAddOnDetailsOpen}
                      />

                      <PdpDeliveryAvailability
                        product={product}
                        tooltipText={deliveryAvailabilityTooltip}
                      />
                      {/* Want it sooner promo — stays in variations column; scrolls to carousel below accordion */}
                      {instockProducts?.length > 0 && (
                        <WantItSoonerPromo carouselRef={wantItSoonerCarouselRef} instockProducts={instockProducts} />
                      )}
                    </>
                  )}

                  <Box ref={errorContainerRef} className="error-container">
                    {!showLoading && showOptionsMessage && (
                      <Fade in={true}>
                        <Text {...styles.errorMessage}>
                          <FormattedMessage
                            defaultMessage="Please select all your options above"
                            id="product_view.options.message"
                          />
                        </Text>
                      </Fade>
                    )}
                  </Box>
                  <HideOnDesktop>
                    {showFullLink && product && getProductUrl(product) && (
                      <Link
                        {...styles.fullDetailsLink}
                        to={getProductUrl(product)}
                      >
                        <FormattedMessage
                          defaultMessage="See full details"
                          id="product_view.link.full_details"
                        />
                      </Link>
                    )}
                  </HideOnDesktop>
                  {isProductASet && <p>{product?.shortDescription}</p>}
                </VStack>

                {/* Product Action Section: In Stock, Total Price, Add to Cart */}
                {!isProductPartOfBundle && !isProductPartOfSet && (
                  <Box className="product-action-section" {...productActionSectionStyles}>
                    <VStack align="stretch" spacing={4}>
                      {/* Stock indicator - Simple products only */}
                      {isSimpleProduct && (
                        <ProductStockIndicator
                          product={product}
                          shopperInventoryIds={shopperInventoryIds}
                          variant={isSimpleMasterWithCovers ? freeformSelectedVariantEntry : variant}
                          onNotifyMeClick={onNotifyMeClick}
                        />
                      )}

                      {/* Total Price Section */}
                      <Box id={PDP_PRICE_SECTION_ID} {...styles.pdpPriceSection}>
                        {isComplexProduct ? (
                          <ComplexTotalPrice
                            currency={product?.currency || activeCurrency}
                            depositProduct={depositSourceProduct}
                            intl={intl}
                            isDepositLoading={isPdpInventoryLoading}
                            product={product}
                            styles={styles}
                          />
                        ) : (
                        // Simple PDP — both with configurable options (KINDW-1119, drives `customizeTotal`)
                        // and plain simple (uses `priceData` from OOTB getPriceData) flow through the same
                        // shared TotalPriceBlock so the saving badge + crossed-out list price render
                        // identically to Complex PDPs whenever there's a discount.
                        //
                        // For plain simple products the OOTB `priceData.listPrice` is sourced from
                        // SCAPI tieredPrices — King Living instead authors RRP / list values into the
                        // `c_priceProcessed.listPrice` custom attribute, so we fall back to
                        // `getProductListPrice(product)` to surface the saving badge consistently.
                          <TotalPriceBlock
                            currency={product?.currency || activeCurrency}
                            depositProduct={depositSourceProduct}
                            intl={intl}
                            isDepositLoading={isPdpInventoryLoading}
                            saleTotal={(useCustomizePricing ? customizeTotal?.currentPrice : priceData?.currentPrice) ?? 0}
                            styles={styles}
                            listTotal={(useCustomizePricing ?
                              customizeTotal?.listPrice :
                              priceData?.listPrice ?? getProductListPrice(product)) ?? 0}
                          />
                        )}

                        <BuyNowPayLaterMessaging
                          currency={product?.currency || activeCurrency}
                          pageType={BNPL_PAGE_TYPE.PRODUCT}
                          amount={
                            isComplexProduct ?
                              complexPdpSaleTotal :
                              ((useCustomizePricing ? customizeTotal?.currentPrice : priceData?.currentPrice) ?? 0)
                          }
                        />
                      </Box>
                    </VStack>
                  </Box>
                )}

                <Box className="product-action-section-content" {...productActionSectionContentStyles}>
                  <Box className="product-action-section-content-buttons"
                    ref={assignAtcSectionRef}
                    display={{base: showInPageAtcOnMobile ? 'block' : 'none', lg: 'block'}}
                  >
                    {renderActionButtons({primaryCtaRef: assignPrimaryCtaRef})}
                  </Box>

                  {product?.c_pimlyTermsAndConditions && (
                    <Box className="product-action-section-terms-and-conditions" {...styles.termsAndConditions}>
                      <div className="product-action-section-terms-and-conditions-content"
                        dangerouslySetInnerHTML={{
                          __html: product.c_pimlyTermsAndConditions,
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </VStack>
            </InlineCustomizeShell>
          </Flex>

          {/* Portal cover flyout — outside frozen variations-section so React portal clicks
              do not bubble to onClickCapture on data-flyout-frozen (KL-998). */}
          {isSimpleMasterWithCovers && freeformCoverVariants.length > 0 && (
            <CoverSwatchOverflowFlyout
              atcDisplayPrice={coverFlyoutAtcDisplayPrice}
              committedCover={freeformCoverSelection}
              committedSelections={inlineSelectionsDraft ?? committedSelection?.selections}
              coverGroups={coverGroups}
              currency={product?.currency || activeCurrency}
              isOpen={isCoverOverflowFlyoutOpen}
              isSimpleConfigurableMaster={isSimpleConfigurableMaster}
              masterMinPrice={coverMasterMinPrice}
              mobileGalleryLayers={simplePdpGalleryLayers}
              mobileGalleryPreviewProduct={simplePdpMainGalleryDisplay?.previewProduct}
              mobileGalleryVariationAttributes={galleryVariationAttributes}
              optionListPriceMap={setOptionListPriceMap}
              optionPriceMap={setOptionPriceMap}
              product={product}
              variants={freeformCoverVariants}
              onClose={handleCoverOverflowClose}
              onCommit={handleFreeformCoverSelect}
              onGalleryPreviewChange={handleCoverOverflowGalleryPreview}
              onViewDetails={handleCoverViewDetails}
            />
          )}

          {/* Specifications — full width, before Want It Sooner (complex + simple standalone PDP) */}
          {showSpecificationsAccordionInProductView && (
            <InformationAccordion isSimpleProduct={isSimpleProduct} product={product} />
          )}

          {/* Want it sooner carousel — full width, after specifications accordion */}
          {instockProducts?.length > 0 && (
            <WantItSoonerCarousel carouselRef={wantItSoonerCarouselRef} instockProducts={instockProducts} />
          )}

          {/* Floating Add-to-Cart strip (complex, simple+configurable options, or Freeform) — desktop + mobile bottom bar */}
          {usesFloatingAddToCartStrip && (
            <AddToCartStrip
              addCustomizeToCart={addCustomizeToCart}
              ctaElement={inPageCtaElement}
              ctaRef={primaryAddToCartButtonRef}
              currency={product?.currency || activeCurrency}
              hasConfigurableOptions={hasConfigurableOptions}
              isBasketLoading={isBasketLoading}
              isComplexProduct={isComplexProduct}
              isConfigurationSelectorOpen={isConfigurationSelectorOpen}
              isCtaLoading={isCtaLoading}
              isCtaPending={isCtaPending}
              isSimpleFreeForm={isSimpleFreeForm}
              notifyMeMode={effectiveShowNotifyMe}
              priceData={useCustomizePricing ? customizeTotal : priceData}
              product={product}
              shopperInventoryIds={shopperInventoryIds}
              variant={isSimpleMasterWithCovers ? freeformSelectedVariantEntry : variant}
              disableButton={
                disableButton ||
                isAddToCartBlocked ||
                isPdpInventoryLoading ||
                (hasConfigurableOptions && !isCustomizeComplete)
              }
              onAddToCart={handleCartItem}
              onNotifyMeClick={handleFreeformNotifyMeClick}
              onSuccess={handlePostAddToCart}
            />
          )}

          {/* Sticky call-to-action buttons for mobile (simple products without configurable options) */}
          <Box
            {...styles.stickyActionButtons}
            boxShadow={theme.shadows.top}
            opacity={hideMobileStickyCta ? 0 : 1}
            pointerEvents={hideMobileStickyCta ? 'none' : 'auto'}
            transform={hideMobileStickyCta ? 'translateY(100%)' : 'translateY(0)'}
            transition="transform 0.2s ease, opacity 0.2s ease"
            display={
              isProductPartOfSet || isProductPartOfBundle || usesFloatingAddToCartStrip ?
                'none' : {base: 'block', lg: 'none'}
            }
          >
            {renderActionButtons()}
          </Box>
        </Flex>
      </>
    );

    // CustomizeJourneyProvider is now provided at the page level (ProductDetail)
    // so all products can access customize features when needed
    return content;
  }
);

ProductViewContent.displayName = 'ProductViewContent';

const ProductView = forwardRef((props, ref) => (
  <ProductViewContent ref={ref} {...props} />
));

ProductView.displayName = 'ProductView';

ProductView.propTypes = ProductViewContent.propTypes = {
  product: PropTypes.object,
  isProductPartOfSet: PropTypes.bool,
  isProductPartOfBundle: PropTypes.bool,
  childOfBundleQuantity: PropTypes.number,
  category: PropTypes.array,
  isProductLoading: PropTypes.bool,
  isBasketLoading: PropTypes.bool,
  addCustomizeToCart: PropTypes.func,
  addToCart: PropTypes.func,
  updateCart: PropTypes.func,
  showFullLink: PropTypes.bool,
  imageSize: PropTypes.oneOf(['sm', 'md']),
  childProductOrderability: PropTypes.object,
  setChildProductOrderability: PropTypes.func,
  onVariantSelected: PropTypes.func,
  validateOrderability: PropTypes.func,
  showImageGallery: PropTypes.bool,
  setSelectedBundleQuantity: PropTypes.func,
  selectedBundleParentQuantity: PropTypes.number,
  onNotifyMeClick: PropTypes.func,
  addToWishlist: PropTypes.func,
  isWishlistLoading: PropTypes.bool,
};

export default ProductView;
